/*
 * Verano Design : comportements ajoutés au thème, sans modifier assets/theme.js.
 *
 * Les classes du thème sont reprises avec customElements.get() et complétées :
 * chaque méthode enveloppée appelle d'abord la méthode d'origine, puis ajoute
 * le comportement Verano. Une mise à jour du thème reste donc possible.
 * Les textes viennent des fichiers de langue, exposés par snippets/js-variables.liquid :
 * theme.lotStrings, theme.quoteStrings, theme.prixProStrings, theme.nuancierStrings
 * (et theme.variantStrings du thème).
 *
 * Chargé après theme.js (defer), sur toutes les pages.
 *
 * 1. Produits vendus par lot : bouton d'ajout et total qui suivent la quantité.
 * 2. Demande de devis : liste de produits (fiches et panier), panneau, compteur
 *    de l'en-tête, envoi à HubSpot ou par le formulaire de contact de Shopify.
 *    Section professionnelle et encadré « Professionnel ? » de la fiche produit.
 *    Client pro connecté (window.veranoModePrix) : prix et total HT, sans case professionnelle.
 *    À la connexion ou à la déconnexion, les lignes déjà dans la liste reprennent le prix du
 *    visiteur actuel (HT ou TTC).
 * 3. Produits reliés (siblings) : la liste déroulante des modèles ouvre la fiche choisie.
 * 4. Galerie produit : compteur de visuels sur mobile.
 * 5. Nuanciers conditionnels : teintes de tissu ou de finition selon l'option choisie,
 *    aperçu agrandi, envoi dans la commande. Options payantes : supplément ajouté au
 *    panier et tenu d'accord avec le meuble.
 * 5 bis. Remise d'ensemble et client pro : code d'ensemble retiré du panier d'un client pro.
 * 5 ter. Prix par module sur la fiche (offre de composition) : colonnes, relance, code du
 *    palier posé sur le panier à l'ajout.
 * 6. Panneau de composition : image et quantité de la variante choisie,
 *    lignes de lot, remise d'ensemble. Placé en dernier : il s'arrête sur les
 *    pages sans panneau.
 */
// Textes traduits (snippets/verano-textes.liquid : <script type="application/json" id="verano-textes">). Chaque
// appel donne le texte français d'origine en repli : si le bloc manque ou si une clé n'y est pas, rien ne change.
// Les repères [clé] du texte sont remplacés par les valeurs.
const veranoTextes = (() => {
  try {
    return JSON.parse(document.getElementById('verano-textes')?.textContent || '{}') || {};
  } catch (error) {
    return {};
  }
})();
const veranoT = (cle, repli, valeurs = {}) => Object.entries(valeurs)
  .reduce((texte, [nom, valeur]) => texte.split(`[${nom}]`).join(String(valeur)), veranoTextes[cle] || repli);
// Singulier ou pluriel : « cle_un » quand n vaut 1 ou moins, « cle_n » sinon (comme les anciens « s » ajoutés si n > 1).
// Option de couleur reconnue à son nom traduit (fr, de, es, nl, pt, it).
const VERANO_COULEUR = /couleur|farbe|colou?r|kleur|\bcor\b/i;
const veranoTn = (cle, n, repliUn, repliN, valeurs = {}) => (n > 1
  ? veranoT(`${cle}_n`, repliN, { count: n, ...valeurs })
  : veranoT(`${cle}_un`, repliUn, { count: n, ...valeurs }));

(() => {
  const strings = theme.lotStrings;

  // Remplace les repères [clé] d'un texte traduit.
  const fill = (text, values) => Object.entries(values).reduce((out, [key, value]) => out.replaceAll(`[${key}]`, value), text);

  // Remplace une méthode d'une classe du thème ; le wrapper reçoit la méthode d'origine en premier argument.
  const wrap = (Class, name, wrapper) => {
    if (!Class) return;
    const original = Class.prototype[name];
    Class.prototype[name] = function (...args) {
      return wrapper.call(this, original, ...args);
    };
  };

  const formatMoney = (cents, format = theme.settings.moneyFormat) => theme.Currency.formatMoney(cents, format);

  // Remises : un code d'offre s'ajoute à ceux déjà dans le panier au lieu de les remplacer. Il ne
  // chasse que ceux de sa famille, même nom sans le taux (COMPTOIR10 → COMPTOIR15, ENSEMBLE →
  // ENSEMBLE5). Les codes des autres offres restent : ils portent sur d'autres produits (les
  // comptoirs sont hors ENSEMBLE), et Shopify n'applique jamais deux remises sur la même ligne.
  const familleCode = (code) => String(code || '').toUpperCase().replace(/\d+$/, '');
  const poserCode = async (code) => {
    let actuels = [];
    try {
      const panier = await (await fetch(theme.routes.cart_url, theme.utils.fetchConfig('json', 'GET'))).json();
      actuels = (panier.discount_codes || []).map((remise) => remise.code);
    } catch (error) {
      actuels = [];
    }
    const codes = [...actuels.filter((actuel) => familleCode(actuel) !== familleCode(code)), code];
    return fetch(theme.routes.cart_update_url, {
      ...theme.utils.fetchConfig('javascript'),
      body: JSON.stringify({ discount: codes.join(',') })
    });
  };

  window.veranoPoserCode = poserCode;

  // Aperçu du tarif pro sans connexion (snippets/mode-prix.liquid, ?apercu=pro-fr, pro ou
  // particulier), sur un thème non publié seulement : le paramètre est recopié sur les appels GET
  // de la boutique (fenêtre de choix, rendus de section), pour qu'ils s'affichent dans le même mode.
  const apercuPrix = new URLSearchParams(window.location.search).get('apercu');
  if (apercuPrix && window.Shopify?.theme?.role !== 'main') {
    const fetchOrigine = window.fetch.bind(window);
    window.fetch = (ressource, options = {}) => {
      try {
        const methode = (options.method || (ressource instanceof Request ? ressource.method : 'GET')).toUpperCase();
        const adresse = new URL(ressource instanceof Request ? ressource.url : ressource, window.location.origin);
        if (methode === 'GET' && adresse.origin === window.location.origin && !adresse.searchParams.has('apercu')) {
          adresse.searchParams.set('apercu', apercuPrix);
          ressource = ressource instanceof Request ? new Request(adresse, ressource) : adresse.toString();
        }
      } catch (error) {
        // Adresse illisible : l'appel part tel quel.
      }
      return fetchOrigine(ressource, options);
    };
  }

  // Client pro connecté : prix affichés HT (snippets/mode-prix.liquid, exposé par snippets/js-variables.liquid).
  const modePro = !!window.veranoModePrix;
  const prixProStrings = theme.prixProStrings || {};


  /* 1. Produits vendus par lot
     ------------------------------------------------------------ */

  const quantityOf = (formId) => {
    const input = document.querySelector(`input[name="quantity"][form="${formId}"]`);
    return Math.max(parseInt(input?.value, 10) || 1, 1);
  };

  const buttonLabel = (size, plural, quantity) => {
    if (plural) return fill(strings.addPieces, { count: size * quantity, pieces: plural });
    return quantity > 1 ? fill(strings.addLots, { quantity, size }) : fill(strings.addLot, { size });
  };

  // Met à jour les totaux « soit … les N » et le texte des boutons d'ajout des produits vendus par lot.
  const refreshLots = () => {
    document.querySelectorAll('lot-total').forEach((total) => total.update());
    document.querySelectorAll('button[name="add"][data-lot-capable]').forEach((button) => {
      // Depuis le panneau de composition, le bouton garde « Ajouter à la composition ».
      if (button.closest('.quick-view')?.dataset.context === 'bundle') return;

      // La taille du lot suit la variante choisie : elle est lue dans le bloc prix rendu pour cette variante.
      const formId = button.getAttribute('form');
      const total = document.querySelector(`lot-total[data-form="${formId}"]`);
      const size = total ? parseInt(total.dataset.lotSize, 10) : 0;
      const text = button.querySelector('.btn-text>span') || button.querySelector('.btn-text');

      // La taille du lot est toujours suivie (panneau de devis) ; le texte du bouton ne change que si le
      // bloc « Boutons d'achat » règle le bouton des lots sur « Dynamique » (data-lot-dynamic).
      if (size > 1 && button.hasAttribute('data-lot-dynamic')) {
        const label = buttonLabel(size, button.dataset.lotPlural, quantityOf(formId));
        button.dataset.lotSize = size;
        button.dataset.lotLabel = label;
        if (!button.hasAttribute('disabled') && text) text.textContent = label;
      }
      else if (size > 1) {
        button.dataset.lotSize = size;
      }
      else {
        delete button.dataset.lotSize;
        if (button.dataset.lotLabel) {
          delete button.dataset.lotLabel;
          if (!button.hasAttribute('disabled') && text) text.textContent = theme.variantStrings.addToCart;
        }
      }
    });
  };

  if (!customElements.get('lot-total')) {
    customElements.define('lot-total', class extends HTMLElement {
      // Aussi appelé quand la fenêtre d'aperçu rapide insère la fiche produit dans la page.
      connectedCallback() {
        refreshLots();
      }

      update() {
        const size = parseInt(this.dataset.lotSize, 10);
        const quantity = quantityOf(this.dataset.form);
        const format = theme.settings.currencyCodeEnabled ? theme.settings.moneyWithCurrencyFormat : theme.settings.moneyFormat;
        // Client pro : « soit 681,60 € HT les 2 » (data-ht, snippets/lot-price.liquid).
        this.textContent = fill(this.hasAttribute('data-ht') && prixProStrings.totalHt ? prixProStrings.totalHt : strings.total, {
          price: formatMoney(parseInt(this.dataset.lotPrice, 10) * quantity, format),
          count: size * quantity
        });
      }
    });
  }

  // Quand le thème réactive le bouton d'ajout, il remet « Ajouter au panier » : on garde le texte du lot.
  const keepLotLabel = function (original, disable = true, ...args) {
    original.call(this, disable, ...args);

    const button = this.submitButton;
    if (disable || !button?.dataset.lotLabel) return;
    if (button.hasAttribute('data-pre-order') || this.closest('.quick-view')?.dataset.context === 'bundle') return;

    (button.querySelector('.btn-text>span') || button.querySelector('.btn-text')).textContent = button.dataset.lotLabel;
  };
  wrap(customElements.get('product-form'), 'toggleSubmitButton', keepLotLabel);
  wrap(customElements.get('product-sticky-form'), 'toggleSubmitButton', keepLotLabel);

  ['change', 'input'].forEach((type) => {
    document.addEventListener(type, (event) => {
      if (event.target.matches?.('input[name="quantity"]')) refreshLots();
    });
  });
  // Au changement de variante, le thème remet la quantité à son minimum sans émettre d'événement
  // DOM : il publie seulement quantityUpdate.
  ['variantChange', 'quantityUpdate'].forEach((name) => {
    theme.pubsub?.subscribe(theme.pubsub.PUB_SUB_EVENTS[name], () => setTimeout(refreshLots, 50));
  });
  refreshLots();


  /* 2. Demande de devis
     ------------------------------------------------------------ */

  // La liste des produits de la demande est gardée dans le navigateur du client (localStorage),
  // pour survivre aux changements de page. Si le stockage est indisponible (navigation privée),
  // elle reste en mémoire le temps de la page.
  const quoteStrings = theme.quoteStrings;
  const nuancierStrings = theme.nuancierStrings;
  const QUOTE_KEY = 'verano-quote';
  let quoteMemory = [];

  const readQuote = () => {
    try {
      const stored = JSON.parse(window.localStorage.getItem(QUOTE_KEY) || '[]');
      return Array.isArray(stored) ? stored : [];
    } catch (error) {
      return quoteMemory;
    }
  };

  const writeQuote = (items) => {
    quoteMemory = items;
    try {
      window.localStorage.setItem(QUOTE_KEY, JSON.stringify(items));
    } catch (error) {
      // Stockage indisponible : la liste reste en mémoire.
    }
    renderQuote();
  };

  // Ajoute des lignes ; une ligne identique (même variante, mêmes teintes) voit sa quantité augmenter.
  // Le prix d'une ligne est HT pour un client pro (tax-rate.liquid renvoie 0), TTC sinon : la ligne
  // garde sa base (ht: true). Une ligne déjà là dans l'autre base (ajoutée avant connexion ou
  // après déconnexion) reprend le prix et la base de la nouvelle.
  const quoteKey = (item) => `${item.id}|${JSON.stringify(item.options || [])}`;
  const addToQuote = (lines) => {
    const items = readQuote();
    lines.forEach((line) => {
      if (modePro) line.ht = true;
      const existing = items.find((item) => quoteKey(item) === quoteKey(line));
      if (existing) {
        existing.quantity += line.quantity;
        if (!!existing.ht !== !!line.ht) {
          existing.price = line.price;
          if (line.ht) existing.ht = true;
          else delete existing.ht;
        }
      }
      else items.push(line);
    });
    writeQuote(items);
  };

  // Prix affiché seulement dans la base du visiteur : un prix TTC n'est pas montré à un pro, ni un
  // prix HT (tarif pro) à un particulier ; la ligne reste dans la demande, sans prix.
  const prixVisible = (item) => !!item.price && !!item.ht === modePro;

  // Données JSON posées par le Liquid : une valeur absente ou malformée donne le repli au lieu de casser le clic.
  const lireJson = (texte, repli = null) => {
    if (!texte) return repli;
    try {
      return JSON.parse(texte);
    } catch (error) {
      return repli;
    }
  };

  const escapeHtml = (text) => String(text ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));

  // Même étiquette que sur la fiche et dans le panneau de composition : « Lot de 4 tabourets ».
  const lotBadge = (item) => (item.lotPlural
    ? fill(strings.badgePieces, { count: item.lotSize, pieces: item.lotPlural })
    : fill(strings.badge, { count: item.lotSize }));

  const quantityText = (item) => (item.lotSize > 1
    ? fill(quoteStrings.quantityLots, { quantity: item.quantity, size: item.lotSize, count: item.quantity * item.lotSize, pieces: item.lotPlural || '' }).trim()
    : fill(quoteStrings.quantityUnits, { quantity: item.quantity }));

  // Prix de la ligne ; pour un lot, avec le nombre de pièces, comme sur la fiche : « 931,20 € les 8 ».
  // Client pro : « 681,60 € HT les 2 », « 3.208,00 € HT ».
  const linePrice = (item) => {
    const price = formatMoney(item.price * item.quantity);
    if (item.ht && prixProStrings.priceHt) {
      return item.lotSize > 1 ? fill(prixProStrings.lotPriceHt, { price, count: item.lotSize * item.quantity }) : fill(prixProStrings.priceHt, { price });
    }
    return item.lotSize > 1 ? fill(strings.lotPrice, { price, count: item.lotSize * item.quantity }) : price;
  };

  // Teinte choisie sur un nuancier : « Teinte du tissu : Écru (1111) », ou « à choisir ».
  const optionText = (option, withCode) => `${option.name} : ${option.value || nuancierStrings.toChoose}${withCode && option.code ? ` (${option.code})` : ''}`;

  const quoteTotal = (items) => items.reduce((sum, item) => sum + (prixVisible(item) ? item.price * item.quantity : 0), 0);

  // Texte des produits pour l'e-mail et HubSpot : une ligne par produit, puis son lien.
  // Prix en HT pour une ligne ajoutée par un client pro, en TTC sinon.
  const quoteSummary = (items) => items.map((item) => {
    const details = [item.variant, quantityText(item), prixVisible(item) ? `${formatMoney(item.price * item.quantity)} ${item.ht ? 'HT' : 'TTC'}` : ''].filter(Boolean).join(' · ');
    const options = (item.options || []).map((option) => `\n  ${optionText(option, true)}`).join('');
    return `- ${item.product} : ${details}${options}\n  ${item.url}`;
  }).join('\n');

  // Affiche la liste dans le panneau et met à jour le compteur de l'en-tête.
  const renderQuote = () => {
    const items = readQuote();

    document.querySelectorAll('[data-quote-toggle]').forEach((button) => {
      const count = button.querySelector('[data-quote-count]');
      if (count) {
        count.textContent = items.length;
        count.hidden = items.length === 0;
      }
      button.setAttribute('aria-label', items.length ? fill(quoteStrings.headerCount, { count: items.length }) : quoteStrings.headerLabel);
    });

    const quote = document.getElementById('QuoteDrawer');
    if (!quote) return;

    quote.querySelector('[data-quote-items]').innerHTML = items.map((item, index) => `
      <li class="quote-item flex items-center gap-3" data-quote-index="${index}">
        <a href="${escapeHtml(item.url)}" class="quote-item__media media media--square overflow-hidden shrink-0">${item.image ? `<img src="${escapeHtml(item.image)}" alt="" loading="lazy">` : ''}</a>
        <div class="quote-item__info grid gap-1 leading-tight grow">
          <a href="${escapeHtml(item.url)}" class="font-medium">${escapeHtml(item.product)}</a>
          ${item.variant ? `<span class="text-xs text-opacity">${escapeHtml(item.variant)}</span>` : ''}
          ${(item.options || []).map((option) => `<span class="quote-item__option text-xs${option.value ? '' : ' is-missing'}">${escapeHtml(optionText(option))}</span>`).join('')}
          ${item.lotSize > 1 ? `<span class="quote-item__lot badge self-start font-medium leading-tight rounded-full">${escapeHtml(lotBadge(item))}</span>` : ''}
          ${prixVisible(item) ? `<span class="text-sm">${escapeHtml(linePrice(item))}</span>` : ''}
        </div>
        <div class="quote-item__actions grid gap-2 justify-items-end shrink-0">
          <div class="quote-item__quantity flex items-center">
            <button type="button" data-quote-step="-1" aria-label="${escapeHtml(quoteStrings.decrease)}">−</button>
            <span>${item.quantity}</span>
            <button type="button" data-quote-step="1" aria-label="${escapeHtml(quoteStrings.increase)}">+</button>
          </div>
          <button type="button" class="link text-xs" data-quote-remove>${escapeHtml(quoteStrings.remove)}</button>
        </div>
      </li>`).join('');

    quote.querySelector('[data-quote-empty]').hidden = items.length > 0;
    // L'invitation ne sert qu'à la demande vide : avec des produits, la liste et le formulaire passent devant.
    const intro = quote.querySelector('[data-quote-intro]');
    if (intro) intro.hidden = items.length > 0;
    // Total caché tant qu'aucune ligne n'a de prix (produits sur devis uniquement).
    quote.querySelector('[data-quote-total-row]').hidden = !items.some(prixVisible);
    quote.querySelector('[data-quote-total]').textContent = formatMoney(quoteTotal(items));
    syncQuotePro(quote, items);
  };

  // Section professionnelle du panneau : la case « Je suis professionnel » affiche société, SIRET
  // et métier (obligatoires une fois la case cochée). Sans produit dans la liste, le panneau
  // devient une demande de compte pro : titre, présentation et objet de la demande changent.
  const syncQuotePro = (quote, items = readQuote()) => {
    const pro = quote.querySelector('[data-quote-pro]');
    if (!pro) return;
    const actif = pro.checked;
    const compteSeul = actif && items.length === 0;

    quote.querySelector('[data-quote-pro-champs]').hidden = !actif;
    quote.querySelectorAll('[data-quote-pro-requis]').forEach((champ) => {
      champ.disabled = !actif;
      champ.required = actif;
    });

    const titre = quote.querySelector('[data-quote-title]');
    titre.textContent = compteSeul ? titre.dataset.titrePro : titre.dataset.titreDevis;
    const introPro = quote.querySelector('[data-quote-pro-intro]');
    if (introPro) introPro.hidden = !compteSeul;
    const intro = quote.querySelector('[data-quote-intro]');
    if (intro) intro.hidden = items.length > 0 || compteSeul;
    quote.querySelector('[data-quote-empty]').hidden = items.length > 0 || compteSeul;
    // Le produit consulté n'accompagne qu'une demande professionnelle.
    const produitConsulte = quote.querySelector('[data-quote-produit-consulte]');
    if (produitConsulte) produitConsulte.disabled = !actif || !produitConsulte.value;

    const demande = quote.querySelector('[data-quote-demande]');
    demande.value = compteSeul ? demande.dataset.pro : demande.dataset.devis;
  };

  // Affiche le formulaire, le message de réussite ou le message d'échec.
  const showQuoteState = (quote, state) => {
    quote.querySelector('#QuoteForm').hidden = state === 'success';
    quote.querySelector('[data-quote-success]').hidden = state !== 'success';
    quote.querySelector('[data-quote-error]').hidden = state !== 'error';
  };

  // options.pro : ouvre la section professionnelle ; options.produit : produit consulté, joint à la
  // demande (encadré « Professionnel ? » de la fiche produit).
  // Protection anti-spam de Shopify (hCaptcha invisible) : elle bloque l'envoi du formulaire de
  // contact tant qu'elle ne s'y est pas attachée, et ne s'y attache qu'au premier focus dans un
  // champ. Un client connecté a nom, téléphone et e-mail déjà remplis et, sur iPhone, toucher le
  // bouton ne donne pas le focus : l'envoi était bloqué sans message. On la prévient à l'ouverture
  // du panneau, sans la charger sur toutes les pages.
  const preparerCaptcha = (quote) => {
    const form = quote.querySelector('#QuoteForm');
    if (!form || form.dataset.hcaptchaBound || form.dataset.recaptchaBound) return;
    form.querySelector('input:not([type="hidden"]), textarea')?.dispatchEvent(new FocusEvent('focusin', { bubbles: true }));
  };

  const openQuote = (activeElement = null, options = {}) => {
    const quote = document.getElementById('QuoteDrawer');
    if (!quote) return;
    preparerCaptcha(quote);

    if (options.pro) {
      const pro = quote.querySelector('[data-quote-pro]');
      if (pro) pro.checked = true;
    }
    // Le produit consulté ne vaut que pour la demande ouverte depuis l'encadré : toute autre
    // ouverture l'efface (la case « Je suis professionnel », elle, reste comme le client l'a laissée).
    const produitConsulte = quote.querySelector('[data-quote-produit-consulte]');
    if (produitConsulte) produitConsulte.value = options.produit || '';

    // Depuis le panier latéral, on le ferme pour ne pas empiler deux panneaux.
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer?.open) cartDrawer.hide();

    renderQuote();
    showQuoteState(quote, 'form');
    quote.show(activeElement);
  };

  // Pour la composition d'un bar (assets/composition-bar.js) : mêmes lignes, même panneau.
  window.veranoDevis = { ajouter: addToQuote, ouvrir: openQuote };

  // Ligne de devis pour le produit affiché : variante, quantité, lot, image, prix TTC
  // (HT pour un client pro : data-tax-rate vaut alors 0, snippets/quote-call-buttons.liquid).
  const productLine = (button) => {
    const formId = button.dataset.form;
    const container = button.closest('product-info, .quick-view') || document;
    const variantJson = container.querySelector('[data-selected-variant]')?.textContent;
    const variant = lireJson(variantJson);
    if (!variant) return null;

    const imageJson = container.querySelector('[data-selected-variant-image]')?.textContent;
    const addButton = document.querySelector(`button[name="add"][form="${formId}"]`);
    const taxRate = parseInt(button.dataset.taxRate, 10) || 0;

    // Teintes des nuanciers affichés pour ce produit (partie 5), choisies ou encore à choisir.
    const nuanciers = document.querySelector(`[data-nuanciers][data-form-id="${formId}"]`);
    const options = nuanciers ? [...nuanciers.querySelectorAll('[data-nuancier]:not([hidden])')].map((groupe) => ({
      name: groupe.dataset.propriete,
      value: groupe.querySelector('[data-nuancier-teinte]').value,
      code: groupe.querySelector('[data-nuancier-code]').value
    })) : [];

    // Options payantes (partie 5) : le choix rejoint les teintes, le supplément s'ajoute au prix.
    let supplement = 0;
    const supplements = [];
    document.querySelectorAll(`[data-option-payante][data-form-id="${formId}"]`).forEach((option) => {
      const choix = option.querySelector('[data-option-payante-choix]');
      options.push({ name: option.dataset.nom, value: choix.value, code: '' });
      if (choix.value === option.dataset.valeurPayante) {
        supplement += parseInt(option.dataset.supplementTtc, 10) || 0;
        supplements.push({ id: parseInt(option.dataset.supplementId, 10), url: option.dataset.supplementUrl });
      }
    });

    return {
      id: variant.id,
      options,
      product: button.dataset.productTitle,
      variant: variant.title !== 'Default Title' ? variant.title : '',
      quantity: quantityOf(formId),
      lotSize: parseInt(addButton?.dataset.lotSize, 10) || 0,
      lotPlural: addButton?.dataset.lotPlural || '',
      image: lireJson(imageJson) || button.dataset.productImage,
      url: `${button.dataset.productUrl}?variant=${variant.id}`,
      // Même arrondi que le thème pour le passage en TTC. Produit sur devis (data-sans-prix) : ligne sans prix.
      price: button.hasAttribute('data-sans-prix') ? 0 : Math.floor(Math.round(variant.price * (100 + taxRate)) / 100) + supplement,
      // De quoi recalculer le prix si le visiteur change de base (connexion ou déconnexion d'un pro).
      taux: parseInt(button.dataset.taxRateReel, 10) || 0,
      supplements
    };
  };

  document.addEventListener('click', (event) => {
    const addButton = event.target.closest?.('[data-quote-add]');
    if (addButton) {
      const line = productLine(addButton);
      if (line) addToQuote([line]);
      openQuote(addButton);
      return;
    }

    const cartButton = event.target.closest?.('[data-quote-from-cart]');
    if (cartButton) {
      const data = cartButton.parentElement.querySelector('[data-quote-cart]') || document.querySelector('[data-quote-cart]');
      try {
        addToQuote(JSON.parse(data.textContent));
      } catch (error) {
        console.log(error);
      }
      openQuote(cartButton);
      return;
    }

    const toggle = event.target.closest?.('[data-quote-toggle]');
    if (toggle) {
      openQuote(toggle);
      return;
    }

    // Encadré « Professionnel ? » de la fiche produit (snippets/encadre-pro.liquid).
    const demandePro = event.target.closest?.('[data-quote-pro-open]');
    if (demandePro) {
      // Depuis une fenêtre (bloc Fichiers 3D d'un module) : on la ferme, sinon le panneau s'ouvre dessous.
      demandePro.closest('dialog[open]')?.close();
      openQuote(demandePro, { pro: true, produit: `${demandePro.dataset.produit} (${demandePro.dataset.produitUrl})` });
      return;
    }
    // Bouton « Demander un compte pro » d'une page de collection : un bouton du thème dont le lien
    // est #compte-pro (templates/collection.marque.json). La demande part avec la page consultée.
    const demandeProPage = event.target.closest?.('a[href="#compte-pro"]');
    if (demandeProPage) {
      event.preventDefault();
      const titrePage = document.querySelector('h1')?.textContent.trim() || document.title;
      openQuote(demandeProPage, { pro: true, produit: `${titrePage} (${window.location.origin}${window.location.pathname})` });
      return;
    }
    const encadre = event.target.closest?.('[data-encadre-pro]');
    if (encadre && event.target.closest('[data-encadre-pro-fermer], [data-encadre-pro-ouvrir]')) {
      const fermer = !!event.target.closest('[data-encadre-pro-fermer]');
      encadre.querySelector('[data-encadre-pro-boite]').hidden = fermer;
      encadre.querySelector('[data-encadre-pro-ouvrir]').hidden = !fermer;
      try {
        if (fermer) window.localStorage.setItem('verano-encadre-pro', 'ferme');
        else window.localStorage.removeItem('verano-encadre-pro');
      } catch (error) {
        // Stockage indisponible : le choix vaut pour la page seulement.
      }
      return;
    }

    // Quantité et retrait dans le panneau.
    const row = event.target.closest?.('[data-quote-index]');
    if (!row) return;
    const items = readQuote();
    const item = items[parseInt(row.dataset.quoteIndex, 10)];
    if (!item) return;

    const step = event.target.closest('[data-quote-step]');
    if (step) {
      item.quantity = Math.max(item.quantity + parseInt(step.dataset.quoteStep, 10), 1);
      writeQuote(items);
    }
    else if (event.target.closest('[data-quote-remove]')) {
      writeQuote(items.filter((entry) => entry !== item));
    }
  });

  document.addEventListener('change', (event) => {
    if (!event.target.matches?.('[data-quote-pro]')) return;
    const quote = event.target.closest('#QuoteDrawer');
    if (quote) syncQuotePro(quote);
  });

  // Envoi : les produits sont copiés dans le formulaire. Avec un formulaire HubSpot renseigné
  // dans le panneau, la demande part vers HubSpot, qui crée le contact et prévient par e-mail.
  // Sans HubSpot, ou s'il ne répond pas, le formulaire de contact de Shopify prend le relais.
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    const quote = document.getElementById('QuoteDrawer');
    if (form.id !== 'QuoteForm' || !quote) return;

    const items = readQuote();
    const summary = quoteSummary(items);
    const total = items.some(prixVisible) ? formatMoney(quoteTotal(items)) : '';
    form.querySelector('[data-quote-field="items"]').value = summary;
    form.querySelector('[data-quote-field="total"]').value = total;
    form.querySelector('[data-quote-return]').value = `${window.location.pathname}?devis=envoye`;

    // Un seul envoi à la fois, et un bouton qui le dit : l'envoi par le formulaire de Shopify
    // recharge la page et prend plusieurs secondes ; un second clic déclencherait sa protection
    // anti-spam.
    // Marque propre (data-envoi) : le thème pose déjà aria-busy sur le bouton à chaque envoi
    // (hover-button, assets/theme.js), elle ne distingue donc pas un second clic.
    const submit = form.querySelector('[data-quote-submit]');
    if (submit.hasAttribute('data-envoi')) {
      event.preventDefault();
      return;
    }
    submit.setAttribute('data-envoi', '');
    submit.setAttribute('aria-busy', 'true');
    const texte = submit.querySelector('.btn-text');
    const texteInitial = texte?.textContent;
    if (texte && !texte.dataset.texte) texte.dataset.texte = texteInitial;
    if (texte && quoteStrings.sending) texte.textContent = quoteStrings.sending;

    if (!quote.dataset.hubspotPortal || !quote.dataset.hubspotForm) return;

    event.preventDefault();

    const value = (name) => form.querySelector(`[name="${name}"]:not([disabled])`)?.value.trim() || '';
    const details = summary ? `${quoteStrings.itemsHeading}\n${summary}\n${quoteStrings.totalLabel} : ${total}` : '';
    // Demande professionnelle : métier, SIRET et produit consulté rejoignent le message.
    const pro = form.querySelector('[data-quote-pro]');
    const detailsPro = pro?.checked
      ? [...form.querySelectorAll('[data-quote-pro], [data-quote-pro-requis], [data-quote-produit-consulte]')]
        .filter((champ) => !champ.disabled && champ.name !== 'contact[Société]' && champ.value)
        .map((champ) => `${champ.name.replace(/^contact\[|\]$/g, '')} : ${champ.value}`)
        .join('\n')
      : '';
    // Demande depuis un compte pro connecté : marqueur joint au message, société dans « company ».
    const comptePro = form.querySelector('[data-quote-compte-pro]');
    const detailsComptePro = comptePro ? `${comptePro.name.replace(/^contact\[|\]$/g, '')} : ${comptePro.value}` : '';
    const fields = [
      { name: 'firstname', value: value('contact[name]') },
      { name: 'email', value: value('contact[email]') },
      { name: 'phone', value: value('contact[phone]') },
      { name: 'company', value: value('contact[Société]') },
      { name: 'message', value: [detailsComptePro, detailsPro, value('contact[body]'), details].filter(Boolean).join('\n\n') }
    ].filter((field) => field.value);

    try {
      const response = await fetch(`https://api.hsforms.com/submissions/v3/integration/submit/${quote.dataset.hubspotPortal}/${quote.dataset.hubspotForm}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fields, context: { pageUri: window.location.href, pageName: document.title } })
      });
      if (!response.ok) throw new Error(`HubSpot ${response.status}`);
      writeQuote([]);
      showQuoteState(quote, 'success');
    } catch (error) {
      console.log(error);
      form.submit();
    } finally {
      submit.removeAttribute('aria-busy');
      submit.removeAttribute('data-envoi');
      if (texte) texte.textContent = texteInitial;
    }
  });

  // Retour arrière vers une page gardée en cache après un envoi : le bouton redevient cliquable.
  window.addEventListener('pageshow', () => {
    const submit = document.querySelector('#QuoteForm [data-quote-submit]');
    if (!submit?.hasAttribute('data-envoi')) return;
    submit.removeAttribute('data-envoi');
    submit.removeAttribute('aria-busy');
    const texte = submit.querySelector('.btn-text');
    if (texte) texte.textContent = texte.dataset.texte || texte.textContent;
  });

  // Retour du formulaire de contact de Shopify : la demande est envoyée, la liste est vidée et le
  // panneau se rouvre sur le message de réussite ; en cas d'erreur, il se rouvre sur le formulaire.
  const quoteReturn = () => {
    const quote = document.getElementById('QuoteDrawer');
    if (!quote) return;
    const params = new URLSearchParams(window.location.search);
    const sent = params.get('devis') === 'envoye';
    const failed = window.location.hash === '#QuoteForm';
    if (!sent && !failed) return;

    // Le marqueur est retiré de l'adresse : un rafraîchissement ne rejoue pas le retour.
    params.delete('devis');
    const query = params.toString();
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${query ? `?${query}` : ''}`);

    if (sent) {
      writeQuote([]);
      showQuoteState(quote, 'success');
      setTimeout(() => quote.show(), 500);
    }
    else {
      setTimeout(() => openQuote(), 500);
    }
  };

  // Liste modifiée dans un autre onglet : compteur et panneau suivent.
  window.addEventListener('storage', (event) => {
    if (event.key === QUOTE_KEY) renderQuote();
  });

  // Connexion ou déconnexion d'un pro : les lignes enregistrées dans l'autre base (TTC pour un
  // pro, HT pour un particulier) reprennent le prix que Shopify donne au visiteur actuel (tarif pro
  // du catalogue B2B, ou prix public), options payantes comprises. En cas d'échec, la ligne reste
  // sans prix, comme avant.
  const prixVariante = async (url, id) => {
    const produit = await (await fetch(`${url.split('?')[0]}.js`)).json();
    return produit.variants.find((variante) => variante.id === id)?.price;
  };
  // Les nouveaux prix sont calculés à part (clé quoteKey), puis la liste est relue juste avant
  // l'écriture : une ligne ajoutée ou retirée pendant les requêtes n'est pas écrasée.
  const aBasculer = (item) => item.price && !!item.ht !== modePro;
  const realignerDevis = async () => {
    const aRevoir = readQuote().filter(aBasculer);
    if (!aRevoir.length) return;
    const nouveauxPrix = new Map();
    await Promise.all(aRevoir.map(async (item) => {
      try {
        const prix = [await prixVariante(item.url, item.id)];
        for (const supplement of item.supplements || []) prix.push(await prixVariante(supplement.url, supplement.id));
        if (prix.some((montant) => typeof montant !== 'number')) return;
        const taux = modePro ? 0 : (item.taux || 20);
        nouveauxPrix.set(quoteKey(item), prix.reduce((total, montant) => total + Math.floor(Math.round(montant * (100 + taux)) / 100), 0));
      } catch (error) {
        // Produit introuvable ou réseau indisponible : la ligne garde son ancien prix, non affiché.
      }
    }));
    if (!nouveauxPrix.size) return;
    const items = readQuote();
    let modifie = false;
    items.forEach((item) => {
      const cle = quoteKey(item);
      if (!nouveauxPrix.has(cle) || !aBasculer(item)) return;
      item.price = nouveauxPrix.get(cle);
      if (modePro) item.ht = true;
      else delete item.ht;
      modifie = true;
    });
    if (modifie) writeQuote(items);
  };

  renderQuote();
  realignerDevis();
  quoteReturn();


  /* 3. Produits reliés (siblings)
     ------------------------------------------------------------ */

  // Chaque modèle est un produit à part : choisir un modèle ouvre sa fiche (snippets/product-variants.liquid).
  document.addEventListener('change', (event) => {
    const select = event.target.closest?.('[data-sibling-select]');
    if (select && select.value && select.value !== window.location.pathname) window.location.href = select.value;
  });


  /* 4. Galerie produit
     ------------------------------------------------------------ */

  // Compteur « 2 / 9 » sur la photo : il suit la vignette active, que le client change de visuel
  // en faisant défiler ou en touchant une vignette (snippets/product-media-gallery.liquid).
  const bindGalleryCounters = () => document.querySelectorAll('[data-gallery-counter]').forEach((counter) => {
    if (counter.dataset.galleryBound) return;

    // Vignettes de cette galerie seulement : une page peut en afficher plusieurs (fenêtre d'aperçu).
    const list = counter.closest('.product__gallery')?.querySelector('.product__thumbnails-list');
    if (!list) return;

    const refresh = () => {
      const thumbnails = [...list.querySelectorAll('.product__thumbnail')];
      const current = thumbnails.findIndex((thumbnail) => thumbnail.getAttribute('aria-current') === 'true');
      if (current < 0) return;
      counter.textContent = `${current + 1} / ${counter.dataset.galleryTotal}`;
    };

    refresh();
    new MutationObserver(refresh).observe(list, { subtree: true, attributes: true, attributeFilter: ['aria-current'] });
    counter.dataset.galleryBound = 'true';
  });

  bindGalleryCounters();

  // La fenêtre « Choisir des options » réécrit son contenu à chaque ouverture et le vide à la
  // fermeture : son compteur est neuf à chaque fois, il faut le rebrancher (theme.js, quickview()).
  document.addEventListener('quickview:loaded', bindGalleryCounters);


  /* 5. Nuanciers conditionnels et mentions de teinte
     ------------------------------------------------------------ */

  // Variante choisie, lue dans le sélecteur du même produit (fiche ou fenêtre d'options).
  const nuancierVariant = (wrapper) => {
    const scope = wrapper.closest('quick-view, .product__info') || document;
    try {
      return JSON.parse(scope.querySelector('[data-selected-variant]')?.textContent || 'null');
    } catch (error) {
      return null;
    }
  };

  // SKU complet : SKU de la variante suivi des codes des teintes choisies, dans l'ordre d'affichage.
  const nuancierSku = (wrapper, variant) => {
    const input = wrapper.querySelector('[data-nuanciers-sku]');
    const codes = [...wrapper.querySelectorAll('[data-nuancier]:not([hidden]) [data-nuancier-code]')].map((code) => code.value).filter(Boolean);
    input.value = variant?.sku && codes.length ? [variant.sku, ...codes].join('_') : '';
    input.disabled = !input.value;
  };

  // Affiche le nuancier de la valeur choisie ; seuls ses champs partent dans la commande.
  const syncNuanciers = (wrapper) => {
    const variant = nuancierVariant(wrapper);
    if (!variant) return;

    wrapper.querySelectorAll('[data-nuancier]').forEach((groupe) => {
      const position = parseInt(groupe.dataset.optionPosition, 10);
      // Position 0 : règle sans option, nuancier toujours affiché.
      groupe.hidden = position !== 0 && variant.options?.[position - 1] !== groupe.dataset.optionValue;
      groupe.querySelectorAll('[data-nuancier-teinte], [data-nuancier-code]').forEach((input) => {
        input.disabled = groupe.hidden || !input.value;
      });
    });
    wrapper.querySelector('[data-nuanciers-erreur]').hidden = true;
    nuancierSku(wrapper, variant);

    // Une seule teinte possible (verre de la Faz), ou nuancier toujours affiché (règle sans
    // option) : la première teinte est choisie d'office.
    wrapper.querySelectorAll('[data-nuancier]:not([hidden])').forEach((groupe) => {
      const pastilles = groupe.querySelectorAll('[data-nuancier-pastille]');
      if (groupe.querySelector('[data-nuancier-teinte]').value) return;
      if (pastilles.length && (pastilles.length === 1 || groupe.dataset.optionPosition === '0')) choisirTeinte(pastilles[0]);
    });
  };

  // Pastilles qui dépendent d'une autre option (les couleurs laquées quand Finition = Laquée) :
  // chaque pastille porte ses variantes, calculées par snippets/pastille-option.liquid.
  const syncPastilles = (root = document) => root.querySelectorAll('variant-picker').forEach((picker) => {
    const pastilles = picker.querySelectorAll('[data-pastilles]');
    if (!pastilles.length) return;

    let variant = null;
    try {
      variant = JSON.parse(picker.querySelector('[data-selected-variant]')?.textContent || 'null');
    } catch (error) {
      return;
    }
    if (!variant) return;

    pastilles.forEach((pastille) => {
      let conditions = [];
      try {
        conditions = JSON.parse(pastille.dataset.pastilles);
      } catch (error) {
        return;
      }
      const condition = conditions.find((candidate) => candidate.v.includes(variant.options[candidate.p - 1]));
      const image = condition ? condition.i : pastille.dataset.pastilleDefaut;
      if (!image) return;
      pastille.classList.add('with-image');
      pastille.style.setProperty('--swatch-background-image', `url(${image})`);
      const grande = condition ? condition.g : pastille.dataset.pastilleDefautGrande;
      if (grande) {
        pastille.dataset.apercu = grande;
        pastille.dataset.vignette = image;
      }
    });
  });

  // Mentions des valeurs d'une option native (les pieds en frêne), sous le sélecteur de variantes.
  // Même donnée que les mentions d'un nuancier, autre chemin : snippets/mentions-option.liquid écrit
  // toutes les lignes possibles, on n'affiche que celles de la variante choisie. Une ligne peut
  // dépendre d'une seconde option (les couleurs laquées quand Finition = Laquée), et deux règles
  // peuvent donner la même phrase : elle n'est montrée qu'une fois.
  const syncMentionsOption = (root = document) => root.querySelectorAll('[data-mentions-option]').forEach((bloc) => {
    // La variante du produit auquel ce bloc appartient : le sélecteur le plus proche en remontant,
    // jamais le premier de la page. Une carte de collection porte son propre sélecteur, et lire
    // celui de la fiche ouverte masquait ses mentions à tort.
    let source = null;
    for (let noeud = bloc.parentElement; noeud && !source; noeud = noeud.parentElement) {
      source = noeud.querySelector(':scope [data-selected-variant]');
    }
    let variant = null;
    try {
      variant = JSON.parse(source?.textContent || 'null');
    } catch (error) {
      return;
    }
    if (!variant) return;

    const vues = new Set();
    bloc.querySelectorAll('[data-mention-option]').forEach((ligne) => {
      let visible = variant.options?.[parseInt(ligne.dataset.optionPosition, 10) - 1] === ligne.dataset.optionValue;
      if (visible && ligne.dataset.conditionPosition) {
        try {
          visible = JSON.parse(ligne.dataset.conditionValeurs || '[]')
            .includes(variant.options?.[parseInt(ligne.dataset.conditionPosition, 10) - 1]);
        } catch (error) {
          visible = false;
        }
      }
      if (visible && vues.has(ligne.textContent)) visible = false;
      if (visible) vues.add(ligne.textContent);
      ligne.hidden = !visible;
    });
    bloc.hidden = !vues.size;
  });

  const syncAllNuanciers = (root = document) => {
    root.querySelectorAll('[data-nuanciers]').forEach(syncNuanciers);
    syncPastilles(root);
    syncMentionsOption(root);
  };

  // Aperçu agrandi, centré au-dessus de la pastille sans sortir de la fenêtre.
  // Grandes images déjà téléchargées. Un nuancier précharge les siennes au premier survol ou au
  // premier toucher, pour que l'aperçu suive un passage rapide d'une pastille à l'autre.
  const apercusCharges = new Set();

  const chargerApercu = (url, rappel, priorite) => {
    const image = new Image();
    if (priorite) image.fetchPriority = priorite;
    image.onload = () => {
      apercusCharges.add(url);
      rappel?.();
    };
    image.src = url;
  };

  // Les images du groupe attendent un temps mort : ce sont des images « au cas où », elles ne
  // doivent disputer ni le réseau ni le fil d'exécution à ce que le client est en train de faire.
  const auRepos = (travail) => (window.requestIdleCallback
    ? requestIdleCallback(travail, { timeout: 2000 })
    : setTimeout(travail, 300));

  // Deux sortes de pastilles ont l'aperçu : celles des nuanciers et celles des options Shopify
  // (Couleur, tapis…), rendues par product-variant-picker-swatch.liquid.
  // Les teintes du plateau du configurateur de bar (composition-bar.liquid) aussi.
  // Le médaillon d'une valeur combinée (la corde) passe avant la pastille qui le porte.
  const PASTILLE_AVEC_APERCU = '[data-nuancier-pastille], .pastille-detail[data-apercu], .color-swatch[data-apercu], [data-compo-teinte][data-apercu]';
  const groupeDe = (pastille) => pastille.closest('[data-nuancier], [data-pastilles-groupe]');

  const prechargerApercus = (groupe) => {
    if (!groupe || groupe.dataset.apercusPrecharges) return;
    groupe.dataset.apercusPrecharges = 'true';
    auRepos(() => {
      groupe.querySelectorAll('[data-apercu]').forEach((pastille) => {
        if (!apercusCharges.has(pastille.dataset.apercu)) chargerApercu(pastille.dataset.apercu, null, 'low');
      });
    });
  };

  // Mentions d'une teinte (metaobject mention) : ce qu'il faut savoir avant de la choisir.
  // Version longue sous les pastilles, version courte dans l'aperçu au survol.
  const mentionsDe = (pastille) => {
    try {
      return JSON.parse(pastille.dataset.mentions || '[]');
    } catch (error) {
      return [];
    }
  };

  // Un seul encart pour la page, rattaché à <body> : la colonne produit a un transform qui
  // enfermerait l'encart sous l'en-tête collant. En position fixe, il passe au-dessus.
  let boite = null;
  const boiteApercu = () => {
    if (!boite) {
      boite = document.createElement('div');
      boite.className = 'nuancier__apercu';
      boite.setAttribute('aria-hidden', 'true');
      boite.innerHTML = '<img class="nuancier__apercu-image" alt="" width="480" height="480" data-nuancier-apercu-image><span class="nuancier__apercu-nom text-sm" data-nuancier-apercu-nom></span><span class="nuancier__apercu-mentions text-xs" data-nuancier-apercu-mentions hidden></span>';
      document.body.appendChild(boite);
    }
    return boite;
  };

  const montrerApercu = (pastille) => {
    const url = pastille.dataset.apercu;
    if (!url) return;
    const apercu = boiteApercu();

    // Tant que la grande image n'est pas arrivée, l'encart montre la vignette de la pastille, déjà
    // chargée : jamais la teinte précédente.
    const image = apercu.querySelector('[data-nuancier-apercu-image]');
    if (image.dataset.teinte !== url) {
      image.dataset.teinte = url;
      const vignette = pastille.querySelector('img')?.currentSrc || pastille.dataset.vignette;
      if (apercusCharges.has(url) || !vignette) {
        image.src = url;
      } else {
        image.src = vignette;
        chargerApercu(url, () => {
          if (image.dataset.teinte === url) image.src = url;
        });
      }
    }
    apercu.querySelector('[data-nuancier-apercu-nom]').textContent = pastille.dataset.teinte || pastille.title;

    const mentionsApercu = apercu.querySelector('[data-nuancier-apercu-mentions]');
    const courtes = mentionsDe(pastille).map((mention) => mention.c).filter(Boolean);
    mentionsApercu.textContent = courtes.join(' ');
    mentionsApercu.hidden = !courtes.length;

    // Position mesurée à l'écran : au-dessus de la pastille, en-tête compris ; en dessous seulement
    // si l'encart sortirait de la fenêtre. Recalé dans la largeur de la fenêtre.
    const rect = pastille.getBoundingClientRect();
    const largeur = apercu.offsetWidth;
    const centre = rect.left + rect.width / 2;
    const gauche = Math.min(Math.max(centre - largeur / 2, 8), document.documentElement.clientWidth - largeur - 8);
    const dessous = rect.top - apercu.offsetHeight - 20 < 0;
    apercu.classList.toggle('is-dessous', dessous);
    apercu.style.left = `${gauche}px`;
    apercu.style.top = `${dessous ? rect.bottom : rect.top}px`;
    apercu.style.setProperty('--fleche', `${centre - gauche}px`);
    apercu.classList.add('is-visible');
  };

  // L'encart est fixe : il se ferme dès que la page défile.
  const cacherApercus = () => boite?.classList.remove('is-visible');
  window.addEventListener('scroll', cacherApercus, { passive: true });

  const choisirTeinte = (pastille) => {
    const groupe = pastille.closest('[data-nuancier]');
    const wrapper = groupe.closest('[data-nuanciers]');
    groupe.querySelectorAll('[data-nuancier-pastille]').forEach((autre) => autre.setAttribute('aria-checked', autre === pastille ? 'true' : 'false'));

    const teinte = groupe.querySelector('[data-nuancier-teinte]');
    const code = groupe.querySelector('[data-nuancier-code]');
    teinte.value = pastille.dataset.teinte;
    code.value = pastille.dataset.code;
    teinte.disabled = code.disabled = groupe.hidden;

    // Mentions de la teinte choisie, sous les pastilles. Elles restent sous les yeux jusqu'à
    // l'ajout au panier, là où l'aperçu au survol n'existe pas sur écran tactile.
    const bloc = groupe.querySelector('[data-nuancier-mentions]');
    if (bloc) {
      const longues = mentionsDe(pastille).map((mention) => mention.l).filter(Boolean);
      bloc.replaceChildren(...longues.map((texte) => {
        const ligne = document.createElement('p');
        ligne.className = 'nuancier__mention';
        ligne.textContent = texte;
        return ligne;
      }));
      bloc.hidden = !longues.length;
    }

    wrapper.querySelector('[data-nuanciers-erreur]').hidden = true;
    nuancierSku(wrapper, nuancierVariant(wrapper));
  };

  // Sur ordinateur, l'aperçu suit le survol ; au toucher, il accompagne le choix de la teinte.
  const survolFin = window.matchMedia('(hover: hover) and (pointer: fine)');

  // L'aperçu n'ouvre qu'après un arrêt sur la pastille. Sans ce délai, le survol qui précède le
  // clic ouvre l'encart et charge sa grande image au moment même où le client clique : la sélection
  // est bien enregistrée, mais le contour noir n'est repeint qu'une seconde plus tard, sur une
  // fiche qui porte plusieurs nuanciers. Un clic franc annule l'ouverture en attente.
  const DELAI_APERCU = 180;
  let minuterieApercu = null;
  const annulerApercuEnAttente = () => {
    clearTimeout(minuterieApercu);
    minuterieApercu = null;
  };


  document.addEventListener('click', (event) => {
    const pastille = event.target.closest('[data-nuancier-pastille]');
    const aide = event.target.closest('[data-nuancier-aide]');

    if (pastille) {
      annulerApercuEnAttente();
      choisirTeinte(pastille);
      if (!survolFin.matches) {
        montrerApercu(pastille);
      }
      return;
    }

    // Médaillon touché ou cliqué : l'aperçu agrandi de la corde, et rien d'autre. Le clic ne va pas
    // jusqu'à la pastille : la valeur choisie ne change pas.
    const detail = event.target.closest('.pastille-detail[data-apercu]');
    if (detail) {
      event.preventDefault();
      annulerApercuEnAttente();
      prechargerApercus(groupeDe(detail));
      montrerApercu(detail);
      return;
    }

    // Pastille d'option Shopify touchée sur téléphone : le thème choisit la valeur, on montre l'aperçu.
    const pastilleOption = event.target.closest('.color-swatch[data-apercu], [data-compo-teinte][data-apercu]');
    if (pastilleOption && !survolFin.matches) {
      prechargerApercus(groupeDe(pastilleOption));
      montrerApercu(pastilleOption);
      return;
    }
    if (aide) {
      aide.parentElement.classList.toggle('is-open');
      return;
    }
    cacherApercus();
    document.querySelectorAll('.nuancier__aide.is-open').forEach((bulle) => bulle.classList.remove('is-open'));
  });

  document.addEventListener('mouseover', (event) => {
    if (!survolFin.matches) return;
    const pastille = event.target.closest?.(PASTILLE_AVEC_APERCU);
    if (!pastille) return;
    annulerApercuEnAttente();
    minuterieApercu = setTimeout(() => {
      montrerApercu(pastille);
      prechargerApercus(groupeDe(pastille));
    }, DELAI_APERCU);
  });

  document.addEventListener('touchstart', (event) => {
    prechargerApercus(event.target.closest?.('[data-nuancier], [data-pastilles-groupe]'));
  }, { passive: true });

  document.addEventListener('mouseout', (event) => {
    if (!survolFin.matches) return;
    const pastille = event.target.closest?.(PASTILLE_AVEC_APERCU);
    if (pastille && !pastille.contains(event.relatedTarget)) {
      annulerApercuEnAttente();
      cacherApercus();
    }
  });

  // Pas d'ajout au panier sans teinte : le client est ramené au nuancier incomplet.
  document.addEventListener('submit', (event) => {
    // getAttribute : le formulaire produit a un champ nommé « id » (la variante), qui masque form.id.
    const formId = event.target.getAttribute?.('id');
    if (!formId) return;

    for (const wrapper of document.querySelectorAll(`[data-nuanciers][data-form-id="${formId}"]`)) {
      const manquant = [...wrapper.querySelectorAll('[data-nuancier]:not([hidden])')].find((groupe) => !groupe.querySelector('[data-nuancier-teinte]').value);
      if (!manquant) continue;

      event.preventDefault();
      event.stopImmediatePropagation();
      const erreur = wrapper.querySelector('[data-nuanciers-erreur]');
      erreur.textContent = fill(nuancierStrings.required, { nuancier: manquant.querySelector('.nuancier__titre span').textContent });
      erreur.hidden = false;
      manquant.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
  }, true);

  // Resynchronisation au changement de variante : immédiatement, puis après le rendu serveur
  // que le thème utilise au-delà de 250 variantes.
  theme.pubsub?.subscribe(theme.pubsub.PUB_SUB_EVENTS.variantChange, () => setTimeout(syncAllNuanciers, 50));
  document.addEventListener('change', (event) => {
    if (!event.target.closest('variant-picker, .product-form__input, .variant-wrapper')) return;
    setTimeout(syncAllNuanciers, 50);
    setTimeout(syncAllNuanciers, 600);
  });
  document.addEventListener('quickview:loaded', () => syncAllNuanciers());
  syncAllNuanciers();

  // Options payantes (snippets/options-payantes.liquid) : la valeur payante coche l'article lié
  // du thème (supplément, name="bundles") et ajoute son prix TTC au prix affiché de la fiche.
  // Prix affichés : on garde le texte rendu par le thème (data-prix-base) et on le remplace par
  // base + supplément ; si le thème réécrit le prix (changement de variante), la base est relue.
  const montantDe = (texte) => {
    const trouve = texte.match(/\d[\d.\s  ]*,\d{2}/);
    return trouve ? Math.round(parseFloat(trouve[0].replace(/[.\s  ]/g, '').replace(',', '.')) * 100) : null;
  };

  const prixAvecSupplement = (element, supplement) => {
    if (element.dataset.prixAffiche !== element.textContent) element.dataset.prixBase = element.textContent;
    const base = element.dataset.prixBase;
    const montant = montantDe(base);
    if (montant === null) return;
    element.textContent = supplement ? formatMoney(montant + supplement) : base;
    element.dataset.prixAffiche = element.textContent;
  };

  // Option à choix (snippets/options-choix.liquid), par exemple le plateau d'un comptoir : le bloc
  // reprend la variante, le prix et le nom du choix sélectionné dans la liste, comme une option payante dont la
  // « valeur payante » serait ce choix ; un choix sans supplément (Sans, Inclus) ne coûte rien.
  // Le nuancier du choix s'affiche, la teinte cochée part en propriétés (libellé et code masqué).
  // Choix et teinte du client gardés ici : le thème redessine le bloc à chaque variante.
  const memoOptionsChoix = {};

  const majOptionChoix = (option) => {
    const memo = memoOptionsChoix[`${option.dataset.formId}|${option.dataset.nom}`] || {};
    const liste = option.querySelector('[data-option-choix-select]');
    if (liste && memo.choix && [...liste.options].some((element) => element.value === memo.choix)) liste.value = memo.choix;
    const radio = liste?.selectedOptions[0] || liste?.options[0];
    const choix = option.querySelector('[data-option-payante-choix]');
    if (!radio || !choix) return;

    const id = radio.dataset.supplementId || '';
    option.dataset.supplementId = id;
    option.dataset.supplementTtc = radio.dataset.supplementTtc || '0';
    option.dataset.supplementUrl = radio.dataset.supplementUrl || '';
    option.dataset.valeurPayante = id ? radio.value : '';
    choix.value = radio.value;
    choix.disabled = !id && !radio.hasAttribute('data-inclus');
    option.querySelector('[data-option-payante-supplement]').value = id;
    option.querySelector('[data-option-payante-lien]').value = id;

    let teinte = '';
    let code = '';
    option.querySelectorAll('[data-nuancier-choix]').forEach((nuancier) => {
      const actif = !!radio.dataset.nuancier && nuancier.dataset.nuancierChoix === radio.dataset.nuancier;
      nuancier.hidden = !actif;
      if (!actif) return;
      const teintes = [...nuancier.querySelectorAll('[data-option-choix-teinte-radio]')];
      const memoTeinte = teintes.find((element) => element.value === memo.teinte);
      if (memoTeinte) memoTeinte.checked = true;
      const choisie = teintes.find((element) => element.checked) || teintes[0];
      if (!choisie) return;
      choisie.checked = true;
      teinte = choisie.value;
      code = choisie.dataset.code || '';
      const titre = nuancier.querySelector('[data-option-choix-teinte]');
      if (titre) titre.textContent = teinte;
      const mention = nuancier.querySelector('[data-option-choix-mention]');
      if (mention) {
        mention.textContent = choisie.dataset.mention || '';
        mention.hidden = !choisie.dataset.mention;
      }
    });

    const champTeinte = option.querySelector('[data-option-choix-teinte-champ]');
    const champCode = option.querySelector('[data-option-choix-code-champ]');
    if (champTeinte) {
      champTeinte.value = teinte;
      champTeinte.disabled = !teinte || choix.disabled;
    }
    if (champCode) {
      champCode.value = code;
      champCode.disabled = !code || choix.disabled;
    }
    option.dataset.resume = teinte ? `${radio.value} · ${teinte}` : radio.value;
  };

  // Le thème redessine le bloc à chaque changement de variante : le choix du client est gardé ici.
  const choixOptionsPayantes = {};

  const syncOptionsPayantes = () => {
    const supplements = {};
    document.querySelectorAll('[data-option-choix]').forEach(majOptionChoix);
    document.querySelectorAll('[data-option-payante]').forEach((option) => {
      const formId = option.dataset.formId;
      const choix = option.querySelector('[data-option-payante-choix]');
      const memorise = choixOptionsPayantes[`${formId}|${choix.name}`];
      if (memorise && choix.value !== memorise) choix.value = memorise;
      const payante = choix.value === option.dataset.valeurPayante;
      // Case à cocher (option « Sans / Avec ») : la case suit le choix, le champ ne part qu'une fois coché.
      const coche = option.querySelector('[data-option-payante-case]');
      if (coche) {
        coche.checked = payante;
        choix.disabled = !payante;
      }
      const article = option.querySelector('[data-option-payante-supplement]');
      article.checked = payante;
      article.dataset.min = quantityOf(formId);
      option.querySelector('[data-option-payante-lien]').disabled = !payante;
      supplements[formId] = (supplements[formId] || 0) + (payante ? parseInt(option.dataset.supplementTtc, 10) : 0);
    });

    Object.entries(supplements).forEach(([formId, supplement]) => {
      const cle = formId.replace(/^ProductForm-/, '');
      document.querySelectorAll(`#Price-${CSS.escape(cle)} .price-item, #StickyPrice-${CSS.escape(cle)} .price-item, product-buy-price[form="${formId}"]`)
        .forEach((element) => prixAvecSupplement(element, supplement));
      // Rappel TTC d'un pro soumis à la TVA française (snippets/price-taxes.liquid) : le supplément,
      // HT pour lui, repasse au taux réel du produit, avec le même arrondi que options-payantes.liquid.
      document.querySelectorAll(`#Price-${CSS.escape(cle)} [data-prix-ttc-pro]`).forEach((element) => {
        const taux = parseInt(element.dataset.taux, 10) || 0;
        prixAvecSupplement(element, Math.floor(supplement * (100 + taux) / 100));
      });
    });
  };

  // Variante changée : le thème ne redessine pas ce bloc. Les choix dont le supplément dépend des options
  // de la fiche (variateur selon taille, finition et température) sont relus dans la page rendue pour la
  // nouvelle variante : prix, choix devenus indisponibles, bloc masqué s'il ne reste qu'un choix.
  theme.pubsub?.subscribe(theme.pubsub.PUB_SUB_EVENTS.variantChange, ({ data }) => {
    const rendu = data?.parsedHTML;
    if (!rendu) return;
    document.querySelectorAll('[data-option-choix]').forEach((option) => {
      const source = [...rendu.querySelectorAll('[data-option-choix]')]
        .find((element) => element.dataset.formId === option.dataset.formId && element.dataset.nom === option.dataset.nom);
      const liste = option.querySelector('[data-option-choix-select]');
      const listeSource = source?.querySelector('[data-option-choix-select]');
      if (!liste || !listeSource) return;
      const choisi = liste.value;
      liste.innerHTML = listeSource.innerHTML;
      if ([...liste.options].some((element) => element.value === choisi)) liste.value = choisi;
      else if (memoOptionsChoix[`${option.dataset.formId}|${option.dataset.nom}`]) delete memoOptionsChoix[`${option.dataset.formId}|${option.dataset.nom}`].choix;
      option.hidden = source.hidden;
      majOptionChoix(option);
      const champ = option.querySelector('[data-option-payante-choix]');
      choixOptionsPayantes[`${champ.getAttribute('form')}|${champ.name}`] = champ.value;
    });
    syncOptionsPayantes();
  });

  // Option à choix : un plateau ou une teinte cochés sont gardés, puis tout est recalculé.
  document.addEventListener('change', (event) => {
    if (!event.target.matches?.('[data-option-choix-select], [data-option-choix-teinte-radio]')) return;
    const option = event.target.closest('[data-option-choix]');
    if (!option) return;
    const cle = `${option.dataset.formId}|${option.dataset.nom}`;
    const memo = memoOptionsChoix[cle] || (memoOptionsChoix[cle] = {});
    if (event.target.matches('[data-option-choix-select]')) memo.choix = event.target.value;
    else memo.teinte = event.target.value;
    majOptionChoix(option);
    const choix = option.querySelector('[data-option-payante-choix]');
    choixOptionsPayantes[`${choix.getAttribute('form')}|${choix.name}`] = choix.value;
    syncOptionsPayantes();
  });

  // Fenêtre de choix chargée : ses options à choix prennent leur état de départ (teinte comprise).
  document.addEventListener('quickview:loaded', () => syncOptionsPayantes());

  // Case cochée : elle écrit la valeur dans le champ caché de l'option, qui porte le choix.
  document.addEventListener('change', (event) => {
    if (!event.target.matches?.('[data-option-payante-case]')) return;
    const option = event.target.closest('[data-option-payante]');
    const choix = option.querySelector('[data-option-payante-choix]');
    choix.value = event.target.checked ? option.dataset.valeurPayante : option.dataset.valeurSans;
    choix.disabled = !event.target.checked;
    choix.dispatchEvent(new Event('change', { bubbles: true }));
  });

  ['change', 'input'].forEach((type) => {
    document.addEventListener(type, (event) => {
      if (event.target.matches?.('[data-option-payante-choix]')) {
        choixOptionsPayantes[`${event.target.getAttribute('form')}|${event.target.name}`] = event.target.value;
      }
      if (event.target.matches?.('[data-option-payante-choix], input[name="quantity"]')) syncOptionsPayantes();
    });
  });
  ['variantChange', 'quantityUpdate'].forEach((name) => {
    theme.pubsub?.subscribe(theme.pubsub.PUB_SUB_EVENTS[name], () => {
      setTimeout(syncOptionsPayantes, 50);
      setTimeout(syncOptionsPayantes, 600);
    });
  });
  syncOptionsPayantes();

  // Panier : la quantité de chaque supplément suit celle des lignes qui le demandent
  // (propriétés « _Supplément <option> », une par option payante, et l'ancienne « _Supplément »
  // des lignes déjà en panier). Meuble retiré ou quantité changée : le supplément suit.
  const SOURCE_SUPPLEMENTS = 'verano-supplements';
  let synchroEnCours = false;

  // Compteurs du panier (en-tête, tiroir, barre mobile) : sans les suppléments réclamés par une
  // ligne de meuble, fondus dans sa ligne (snippets/supplements-ligne.liquid). Même calcul que
  // snippets/nombre-articles-panier.liquid, repris après chaque mise à jour du panier.
  const nombreArticles = (cart) => {
    if (!Array.isArray(cart?.items)) return cart?.item_count;
    const reclames = new Set();
    cart.items.forEach((item) => Object.entries(item.properties || {}).forEach(([cle, valeur]) => {
      if (cle.startsWith('_Supplément') && valeur) reclames.add(String(valeur));
    }));
    return cart.items.reduce((total, item) => (
      item.product_type === 'Supplément' && reclames.has(String(item.variant_id)) ? total : total + item.quantity
    ), 0);
  };

  // Écouteur à part : les compteurs déjà affichés ont lié leur méthode avant ce script, un
  // wrap ne les atteindrait pas. Abonné après eux, il repasse derrière le thème.
  theme.pubsub?.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, (event) => {
    if (event?.cart?.errors) return;
    const nombre = nombreArticles(event?.cart);
    if (typeof nombre !== 'number') return;
    document.querySelectorAll('cart-count').forEach((compteur) => {
      compteur.innerText = nombre;
      compteur.hidden = nombre === 0;
    });
  });

  // « Vu récemment » du tiroir (onglet) et de la page panier : affiché panier vide seulement,
  // pour ne pas distraire le client. Le Liquid règle le chargement, ceci suit les ajouts et
  // retraits faits sans recharger la page.
  theme.pubsub?.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, (event) => {
    if (event?.cart?.errors || typeof event?.cart?.item_count !== 'number') return;
    const masquer = event.cart.item_count > 0;
    document.querySelectorAll('[data-vu-recemment-panier]').forEach((bloc) => {
      bloc.hidden = masquer;
      const onglet = bloc.querySelector('[aria-controls][aria-expanded="true"]');
      if (masquer && onglet) bloc.closest('[is="tab-list"]')?.reset();
    });
  });

  const synchroniserSupplements = async (cart) => {
    if (synchroEnCours || !cart?.items) return;
    const besoin = {};
    const presents = {};
    cart.items.forEach((item) => {
      Object.entries(item.properties || {}).forEach(([cle, supplement]) => {
        if (!cle.startsWith('_Supplément') || !supplement) return;
        besoin[supplement] = (besoin[supplement] || 0) + item.quantity;
      });
      if (item.product_type === 'Supplément') presents[item.variant_id] = (presents[item.variant_id] || 0) + item.quantity;
    });

    const updates = {};
    Object.entries(presents).forEach(([variante, quantite]) => {
      if (quantite !== (besoin[variante] || 0)) updates[variante] = besoin[variante] || 0;
    });
    const ajouts = Object.entries(besoin).filter(([variante]) => !presents[variante]).map(([id, quantity]) => ({ id: parseInt(id, 10), quantity }));
    if (!Object.keys(updates).length && !ajouts.length) return;

    synchroEnCours = true;
    try {
      const config = theme.utils.fetchConfig('json');
      const reponses = [];
      if (Object.keys(updates).length) reponses.push(await fetch(`${theme.routes.cart_update_url}.js`, { ...config, body: JSON.stringify({ updates }) }));
      if (ajouts.length) reponses.push(await fetch(`${theme.routes.cart_add_url}.js`, { ...config, body: JSON.stringify({ items: ajouts }) }));
      // Supplément refusé (produit retiré de la vente) : pas de rechargement, qui bouclerait.
      if (reponses.some((reponse) => !reponse.ok)) return;
      if (document.body.classList.contains('template-cart')) {
        window.location.reload();
        return;
      }
      const nouveauPanier = await (await fetch(theme.routes.cart_url, theme.utils.fetchConfig('json', 'GET'))).json();
      theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: SOURCE_SUPPLEMENTS, cart: nouveauPanier });
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false } }));
    } finally {
      synchroEnCours = false;
    }
  };

  theme.pubsub?.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, (event) => {
    if (event?.source !== SOURCE_SUPPLEMENTS) synchroniserSupplements(event?.cart);
  });
  if (document.body.classList.contains('template-cart')) {
    fetch(theme.routes.cart_url, theme.utils.fetchConfig('json', 'GET')).then((reponse) => reponse.json()).then(synchroniserSupplements);
  }


  /* 5 bis. Remise d'ensemble et client pro
     ------------------------------------------------------------ */

  // Remises de composition réservées aux particuliers : un client pro connecté (modePro, lu dans
  // window.veranoModePrix) a déjà ses prix remisés. Si l'un de ces codes arrive dans son panier
  // (tapé à la main, ou posé avant qu'il se connecte), il est retiré aussitôt. ENSEMBLE, plus
  // les codes des paliers des offres de composition (window.veranoCodesParticuliers, écrit par
  // snippets/js-variables.liquid depuis les métaobjets offre_composition).
  const CODES_PARTICULIERS = new Set(['ENSEMBLE', 'ENSEMBLE5', ...(window.veranoCodesParticuliers || [])].map((code) => code.toUpperCase()));
  const SOURCE_ENSEMBLE_PRO = 'verano-ensemble-pro';
  let retraitEnsembleEnCours = false;
  const retirerEnsemblePro = async (panier) => {
    const codes = (panier?.discount_codes || []).map((remise) => remise.code);
    if (retraitEnsembleEnCours || !codes.some((code) => CODES_PARTICULIERS.has(code.toUpperCase()))) return;
    retraitEnsembleEnCours = true;
    try {
      const autres = codes.filter((code) => !CODES_PARTICULIERS.has(code.toUpperCase())).join(',');
      const reponse = await fetch(`${theme.routes.cart_update_url}.js`, { ...theme.utils.fetchConfig('json'), body: JSON.stringify({ discount: autres }) });
      if (!reponse.ok) return;
      if (document.body.classList.contains('template-cart')) {
        window.location.reload();
        return;
      }
      theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: SOURCE_ENSEMBLE_PRO, cart: await reponse.json() });
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false } }));
    } finally {
      retraitEnsembleEnCours = false;
    }
  };
  if (modePro) {
    theme.pubsub?.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event?.source !== SOURCE_ENSEMBLE_PRO) retirerEnsemblePro(event?.cart);
    });
    fetch(theme.routes.cart_url, theme.utils.fetchConfig('json', 'GET')).then((reponse) => reponse.json()).then(retirerEnsemblePro).catch(() => {});
  }


  /* 5 ter. Prix par module sur la fiche (offre de composition)
     ------------------------------------------------------------ */

  // Bloc offre_composition de sections/main-product.liquid : prix TTC de chaque tranche recalculés
  // à chaque variante, colonne de la quantité en cours marquée, ligne « Encore N modules… ».
  // À l'ajout au panier depuis la fiche, le code du palier atteint est posé sur le panier avant
  // l'ajout, comme depuis le panneau de composition (partie 6).
  const offresPrix = () => [...document.querySelectorAll('[data-offre-prix]')];

  const paliersOffre = (bloc) => {
    try {
      return JSON.parse(bloc.dataset.paliers || '[]')
        .map((palier) => ({ n: parseInt(palier.seuil, 10), pct: parseFloat(palier.remise), code: palier.code || '' }))
        .filter((palier) => palier.n > 0 && palier.pct > 0)
        .sort((a, b) => a.n - b.n);
    } catch (error) {
      return [];
    }
  };

  // Même calcul que snippets/ttc-amount.liquid.
  const ttcOffre = (bloc) => {
    const ht = parseInt(bloc.dataset.prixHt, 10) || 0;
    const taux = (parseFloat(bloc.dataset.taux) || 0) + 100;
    return Math.floor(Math.round(ht * taux * 100) / 100 / 100);
  };

  const majOffrePrix = (bloc) => {
    const paliers = paliersOffre(bloc);
    if (!paliers.length) return;
    const prix = ttcOffre(bloc);
    const quantite = quantityOf(bloc.dataset.formId);
    const actuel = [...paliers].reverse().find((palier) => quantite >= palier.n);
    const suivant = paliers.find((palier) => quantite < palier.n);
    const prixRemise = (pct) => Math.round(prix * (100 - pct) / 100);

    const colonnes = [...bloc.querySelectorAll('.product__offre-colonne')];
    colonnes.forEach((colonne, index) => {
      const debut = parseInt(colonne.dataset.debut, 10);
      const debutSuivant = colonnes[index + 1] ? parseInt(colonnes[index + 1].dataset.debut, 10) : Infinity;
      colonne.toggleAttribute('data-actif', quantite >= debut && quantite < debutSuivant);
      const montant = colonne.querySelector('[data-offre-montant]');
      if (montant) montant.innerHTML = formatMoney(prixRemise(parseFloat(colonne.dataset.remise) || 0));
    });

    const texte = bloc.querySelector('[data-offre-relance-texte]');
    if (texte) {
      if (suivant) {
        const manque = suivant.n - quantite;
        texte.innerHTML = veranoTn('relance', manque, 'Encore [count] module pour passer à [prix] le module', 'Encore [count] modules pour passer à [prix] le module', { prix: `<strong>${formatMoney(prixRemise(suivant.pct))}</strong>` });
      }
      else {
        texte.innerHTML = veranoT('meilleur_prix', '[b]Meilleur prix atteint[/b] : −[remise] % sur chaque module', { remise: actuel.pct }).replace('[b]', '<strong>').replace('[/b]', '</strong>');
      }
    }

    // Palier franchi : le bloc s'éclaire un instant.
    const taux = actuel ? actuel.pct : 0;
    if (taux > (bloc.dernierTaux || 0)) {
      bloc.classList.remove('is-nouveau');
      void bloc.offsetWidth;
      bloc.classList.add('is-nouveau');
    }
    bloc.dernierTaux = taux;
    bloc.codeOffre = actuel?.code || '';
  };

  const majOffresDuFormulaire = (formId) => offresPrix()
    .filter((bloc) => !formId || bloc.dataset.formId === formId)
    .forEach(majOffrePrix);

  majOffresDuFormulaire();

  ['change', 'input'].forEach((type) => {
    document.addEventListener(type, (event) => {
      if (event.target.matches?.('input[name="quantity"]')) majOffresDuFormulaire(event.target.getAttribute('form'));
    });
  });

  theme.pubsub?.subscribe(theme.pubsub.PUB_SUB_EVENTS.quantityUpdate, () => setTimeout(() => majOffresDuFormulaire(), 50));

  // Nouvelle variante : nouveau prix de départ.
  theme.pubsub?.subscribe(theme.pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
    const data = event?.data;
    if (!data?.variant) return;
    const formId = `ProductForm-${data.sectionId}-${data.productId}`;
    offresPrix().filter((bloc) => bloc.dataset.formId === formId).forEach((bloc) => {
      if (data.variant.price != null) bloc.dataset.prixHt = data.variant.price;
      majOffrePrix(bloc);
    });
  });

  // Lien « Composez votre bar » : défilement doux jusqu'à la section, au lieu du saut de l'ancre
  // (sauf si le visiteur a demandé moins d'animations). L'arrêt sous l'en-tête fixe vient de
  // scroll-margin-top (assets/verano.css).
  document.addEventListener('click', (event) => {
    const lien = event.target.closest?.('.product__offre-lien[href^="#"]');
    const cible = lien && document.querySelector(lien.getAttribute('href'));
    if (!cible) return;
    event.preventDefault();
    const doux = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    cible.scrollIntoView({ behavior: doux ? 'smooth' : 'auto', block: 'start' });
  });

  // Ajout depuis la fiche : code du palier atteint posé sur le panier, puis l'envoi reprend.
  // Écoute en capture : passe avant le gestionnaire du thème (assets/theme.js, ProductForm).
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    // getAttribute : le champ « id » du formulaire (la variante) masque form.id.
    const bloc = offresPrix().find((element) => element.dataset.formId === form.getAttribute?.('id'));
    if (!bloc) return;
    majOffrePrix(bloc);
    if (!bloc.codeOffre) return;

    if (form.offreReady) {
      form.offreReady = false;
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    try {
      await poserCode(bloc.codeOffre);
    } catch (error) {
      console.log(error);
    }

    form.offreReady = true;
    form.requestSubmit();
  }, true);


  /* 6. Panneau de composition
     ------------------------------------------------------------ */

  const QuickView = customElements.get('quick-view');
  const ProductBundleForm = customElements.get('product-bundle-form');
  const ProductBundle = customElements.get('product-bundle');

  // Image de la composition : gang media, puis image de variante, puis image produit
  // (snippet selected-variant-image).
  const withBundleImage = function (original, ...args) {
    const variant = original.apply(this, args);
    const image = this.querySelector('[data-selected-variant-image]')?.textContent;
    if (variant && image) variant.bundle_image = lireJson(image);
    return variant;
  };
  wrap(ProductBundleForm, 'getSelectedVariant', withBundleImage);

  // Depuis la fenêtre « Choisir des options », la quantité choisie est reportée dans la composition,
  // avec les propriétés du formulaire (teintes des nuanciers, options payantes et leurs liens
  // « _Supplément … ») et le prix des options payantes choisies.
  wrap(QuickView, 'getSelectedVariant', function (original) {
    const variant = withBundleImage.call(this, original);
    if (variant && this.form) {
      const input = this.querySelector(`input[name="quantity"][form="${this.form.getAttribute('id')}"]`) || this.form.querySelector('input[name="quantity"]');
      variant.bundle_quantity = Math.max(parseInt(input?.value, 10) || 1, 1);

      // FormData couvre aussi les champs rattachés par l'attribut form, et ignore les champs
      // désactivés : nuancier masqué, lien de supplément d'une option restée gratuite.
      const properties = {};
      new FormData(this.form).forEach((value, key) => {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match && value !== '') properties[match[1]] = value;
      });

      let supplement = 0;
      const payantes = [];
      this.querySelectorAll('[data-option-payante]').forEach((option) => {
        const choix = option.querySelector('[data-option-payante-choix]');
        if (choix && choix.value === option.dataset.valeurPayante) {
          supplement += parseInt(option.dataset.supplementTtc, 10) || 0;
          // Case « Sans / Avec » : le nom de l'option parle mieux que « Avec ».
          payantes.push(option.querySelector('[data-option-payante-case]') ? option.dataset.nom : (option.dataset.resume || choix.value));
        }
      });

      variant.bundle_properties = Object.keys(properties).length ? properties : null;
      variant.bundle_supplement = supplement;
      variant.bundle_paid_options = payantes;
    }
    return variant;
  });

  if (!ProductBundle) return;

  // Couleur d'un module : la valeur de l'option dont le nom dit « couleur », dans toutes les langues vendues
  // (« Couleur structure », « Gestellfarbe », « Color de la estructura », « Kleur frame », « Cor », « Colore »).
  const couleurDe = (product, variant) => {
    let names = [];
    try {
      names = JSON.parse(product?.dataset.optionNames || '[]');
    } catch (error) {
      names = [];
    }
    const index = names.findIndex((name) => VERANO_COULEUR.test(String(name)));
    return index >= 0 ? variant.options?.[index] : null;
  };

  Object.defineProperties(ProductBundle.prototype, {
    // Paliers de remise, du plus petit au plus grand : ceux de l'offre de composition
    // (data-discount-tiers, métaobjet offre_composition), sinon le seuil unique de la section.
    discountTiers: {
      get() {
        if (this._discountTiers) return this._discountTiers;
        let tiers = [];
        try {
          tiers = JSON.parse(this.dataset.discountTiers || '[]').map((palier) => ({
            n: parseInt(palier.seuil, 10) || 0,
            pct: parseFloat(palier.remise) || 0,
            code: palier.code || ''
          }));
        } catch (error) {
          tiers = [];
        }
        if (!tiers.length && this.dataset.discountCode) {
          tiers = [{
            n: parseInt(this.dataset.discountThreshold, 10) || 0,
            pct: parseFloat(this.dataset.discountPercent) || 0,
            code: this.dataset.discountCode
          }];
        }
        this._discountTiers = tiers.filter((palier) => palier.n > 0 && palier.pct > 0).sort((a, b) => a.n - b.n);
        return this._discountTiers;
      }
    },

    // Nombre de produits différents dans la composition : un lot ou plusieurs coloris comptent pour un.
    distinctProductCount: {
      get() {
        const products = new Set();
        this.variants.forEach((variant) => {
          if (!variant.hasAttribute('available') && variant.product) products.add(variant.product);
        });
        return products.size;
      }
    },

    // Ce que la remise compte. Offre « modules » : chaque pièce compte, deux centraux font deux,
    // et seuls les produits qui portent la même offre entrent dans le compte. Sinon, comme la
    // remise d'ensemble, les produits différents.
    discountCount: {
      get() {
        if (this.dataset.discountCount !== 'modules') return this.distinctProductCount;
        const offre = this.dataset.offre;
        return this.variants.reduce((total, variant) => {
          if (variant.hasAttribute('available') || !variant.product) return total;
          if (offre && variant.product.dataset.offre !== offre) return total;
          return total + (parseInt(variant.querySelector('quantity-input input')?.value, 10) || 1);
        }, 0);
      }
    },

    currentTier: {
      get() {
        const count = this.discountCount;
        return this.discountTiers.filter((palier) => count >= palier.n).pop() || null;
      }
    },

    nextTier: {
      get() {
        const count = this.discountCount;
        return this.discountTiers.find((palier) => count < palier.n) || null;
      }
    }
  });

  Object.assign(ProductBundle.prototype, {
    // Même calcul que updateTotal du thème : prix de chaque ligne × quantité.
    bundleSubtotal() {
      let subtotal = 0;
      this.variants.forEach((variant) => {
        if (!variant.hasAttribute('available')) {
          const price = variant.querySelector('[data-product-bundle-variant-price]');
          const quantityInput = variant.querySelector('quantity-input');
          subtotal += parseInt(price.getAttribute('data-price')) * parseInt(quantityInput.value);
        }
      });
      return subtotal;
    },

    // Offre de composition : les codes ne portent que sur les modules. La remise se calcule donc sur
    // les seules lignes qui portent l'offre (pas les tabourets proposés en fin de composition), hors
    // options payantes (data-supplement).
    bundleDiscountAmount(subtotal) {
      const tier = this.currentTier;
      if (!tier) return 0;
      if (!this.dataset.offre) return Math.round(subtotal * tier.pct / 100);
      const base = this.variants.reduce((total, variant) => {
        if (variant.hasAttribute('available') || variant.product?.dataset.offre !== this.dataset.offre) return total;
        const price = variant.querySelector('[data-product-bundle-variant-price]');
        const quantity = parseInt(variant.querySelector('quantity-input input')?.value, 10) || 1;
        const unitaire = (parseInt(price?.getAttribute('data-price'), 10) || 0) - (parseInt(price?.dataset.supplement, 10) || 0);
        return total + unitaire * quantity;
      }, 0);
      return Math.round(base * tier.pct / 100);
    },

    // Pastille « 2 sélectionnés » sur chaque carte, d'après les lignes du panneau. Le libellé vient
    // de l'offre (libelle_carte) ; [s] y marque le pluriel.
    refreshBundleCounts() {
      const label = this.dataset.countLabel;
      const wrapper = this.closest('.product-bundle-wrapper');
      if (!label || !wrapper) return;
      const counts = new Map();
      this.variants.forEach((variant) => {
        if (variant.hasAttribute('available') || !variant.product) return;
        const quantity = parseInt(variant.querySelector('quantity-input input')?.value, 10) || 1;
        counts.set(variant.product, (counts.get(variant.product) || 0) + quantity);
      });
      wrapper.querySelectorAll('[data-bundle-count]').forEach((pill) => {
        const count = counts.get(pill.closest('.product-card')) || 0;
        pill.hidden = !count;
        const texte = pill.querySelector('[data-bundle-count-texte]') || pill;
        texte.textContent = `${count} ${label.split('[s]').join(count > 1 ? 's' : '')}`;
      });
    },

    // Options sur une ligne, couleur en premier ; « Sans / Avec » complétés par le nom de l'option.
    formatOptions(product, variant) {
      let names = [];
      try {
        names = JSON.parse(product.dataset.optionNames || '[]');
      } catch (error) {
        names = [];
      }
      const options = variant.options.map((value, index) => {
        const name = (names[index] || '').toString();
        const label = /^(sans|avec)$/i.test(value) && name ? `${value} ${name.split(' ').pop().toLowerCase()}` : value;
        return { label, color: VERANO_COULEUR.test(name) };
      });
      return options.sort((a, b) => b.color - a.color).map((option) => option.label).join(' · ');
    },

    // Complète la ligne rendue par le thème : options, étiquette et prix du lot, quantité, taille du prix,
    // options payantes choisies dans la fenêtre et leur prix.
    decorateBundleLine(element, product, variant) {
      const content = element.querySelector('[data-product-bundle-variant-content]');
      const priceElement = content.querySelector('[data-product-bundle-variant-price]');
      const variantPrice = parseInt(priceElement.dataset.price, 10);

      const optionList = content.querySelector('ul');
      if (optionList) {
        if (variant.options[0] === 'Default Title') optionList.remove();
        else optionList.outerHTML = `<p class="text-xs text-opacity leading-tight">${this.formatOptions(product, variant)}</p>`;
      }

      // Produits vendus par lot : étiquette « Lot de N … » et prix à la pièce sous le nom.
      const measurement = variant.unit_price_measurement;
      const variantLot = measurement ? Math.round(parseFloat(measurement.quantity_value)) : 0;
      const lotSize = variantLot || (measurement === undefined ? parseInt(product.dataset.lotSize, 10) : 0);
      if (lotSize > 1) {
        const plural = product.dataset.lotPlural;
        const singular = product.dataset.lotSingular || strings.defaultPiece;
        const lotPrice = fill(strings.lotPrice, { price: formatMoney(variantPrice), count: lotSize });
        const unitPrice = `${formatMoney(Math.round(variantPrice / lotSize))} ${fill(strings.unitLabel, { piece: singular })}`;
        const badge = plural ? fill(strings.badgePieces, { count: lotSize, pieces: plural }) : fill(strings.badge, { count: lotSize });

        priceElement.innerHTML = `<span class="price-item price-item--regular">${lotPrice} · ${unitPrice}</span>`;
        priceElement.insertAdjacentHTML('beforebegin', `<span class="product-bundle__lot-badge badge self-start font-medium leading-tight rounded-full">${badge}</span>`);
        content.classList.add('product-bundle__content--lot');
      }

      // Options payantes : leur nom sous le module, leur prix ajouté à celui de la ligne, que le
      // total du panneau (data-price × quantité) reprend tel quel.
      if (variant.bundle_supplement > 0) {
        priceElement.dataset.price = variantPrice + variant.bundle_supplement;
        priceElement.dataset.supplement = variant.bundle_supplement;
        priceElement.innerHTML = `<span class="price-item price-item--regular">${formatMoney(variantPrice + variant.bundle_supplement)}</span>`;
      }
      if (variant.bundle_paid_options?.length) {
        const liste = document.createElement('p');
        liste.className = 'product-bundle__options text-xs leading-tight';
        liste.textContent = variant.bundle_paid_options.join(' · ');
        priceElement.insertAdjacentElement('beforebegin', liste);
      }

      if (variant.bundle_quantity) {
        const quantityInput = element.querySelector('quantity-input input');
        if (quantityInput) quantityInput.value = variant.bundle_quantity;
      }

      priceElement.classList.remove('text-sm');
      priceElement.classList.add('text-xs', 'product-bundle__line-price');
    }
  });

  wrap(ProductBundle, 'addVariant', function (original, product, variant) {
    const filled = new Set(this.variants.filter((element) => !element.hasAttribute('available')));
    const shown = variant.bundle_image ? { ...variant, featured_media: { preview_image: { src: variant.bundle_image } } } : variant;

    original.call(this, product, shown);

    const element = this.variants.find((item) => !item.hasAttribute('available') && !filled.has(item));
    if (element) {
      // Posé à chaque ajout, même vide : une ligne réutilisée ne garde pas les options de la précédente.
      element.bundleProperties = variant.bundle_properties || null;
      element.bundleColor = couleurDe(product, variant);
      this.decorateBundleLine(element, product, variant);
    }
  });

  wrap(ProductBundle, 'clearVariant', function (original, variant) {
    original.call(this, variant);
    if (variant) {
      variant.bundleProperties = null;
      variant.bundleColor = null;
    }
  });

  // Remise atteinte : total remisé dans le bouton (mobile).
  wrap(ProductBundle, 'updateTotal', function (original) {
    original.call(this);

    const subtotal = this.bundleSubtotal();
    const discount = this.bundleDiscountAmount(subtotal);
    if (discount > 0) this.querySelector('[data-product-bundle-total]').innerHTML = formatMoney(subtotal - discount);
  });

  // Remise atteinte sans récapitulatif : ancien total barré, puis total remisé.
  // Offre de composition : récapitulatif au-dessus du total à la place (data-product-bundle-recap),
  // « 3 modules » et le sous-total, puis la remise du palier atteint et son montant. La ligne de
  // remise s'éclaire un instant quand un palier est franchi.
  wrap(ProductBundle, 'updateTotalWithCurrency', function (original) {
    original.call(this);

    const subtotal = this.bundleSubtotal();
    const discount = this.bundleDiscountAmount(subtotal);
    const recap = this.querySelector('[data-product-bundle-recap]');
    if (recap) {
      const format = theme.settings.moneyWithCurrencyFormat;
      const count = this.discountCount;
      recap.hidden = !count;
      recap.querySelector('[data-product-bundle-recap-count]').textContent = this.dataset.discountCount === 'modules'
        ? veranoTn('nb_modules', count, '[count] module', '[count] modules')
        : veranoTn('nb_produits', count, '[count] produit', '[count] produits');
      recap.querySelector('[data-product-bundle-recap-subtotal]').innerHTML = formatMoney(subtotal, format);

      const ligne = recap.querySelector('[data-product-bundle-recap-remise]');
      const pct = discount > 0 ? this.currentTier.pct : 0;
      ligne.hidden = !pct;
      if (pct) {
        recap.querySelector('[data-product-bundle-recap-remise-label]').textContent = veranoT('remise_libelle', 'Remise −[remise] %', { remise: pct });
        recap.querySelector('[data-product-bundle-recap-remise-amount]').innerHTML = `−${formatMoney(discount, format)}`;
        this.querySelector('[data-product-bundle-total-with-currency]').innerHTML = formatMoney(subtotal - discount, format);
      }
      if (pct > (this.dernierTaux || 0)) {
        ligne.classList.remove('is-nouveau');
        void ligne.offsetWidth;
        ligne.classList.add('is-nouveau');
      }
      this.dernierTaux = pct;
      return;
    }

    if (discount > 0) {
      const format = theme.settings.moneyWithCurrencyFormat;
      this.querySelector('[data-product-bundle-total-with-currency]').innerHTML = `<s class="text-sm font-normal text-opacity">${formatMoney(subtotal, format)}</s> ${formatMoney(subtotal - discount, format)}`;
    }
  });

  // Barre de remise : elle compte ce que l'offre compte (produits différents ou modules) et
  // s'arrête au dernier palier ; chaque palier est un rond posé dans la barre, plein une fois
  // atteint (data-palier, data-atteint). Le texte annonce le palier suivant.
  const remplir = (texte, valeurs) => Object.entries(valeurs).reduce((resultat, [cle, valeur]) => resultat.split(`[${cle}]`).join(valeur), texte || '');

  wrap(ProductBundle, 'updateProgressBar', function (original) {
    const tiers = this.discountTiers;
    if (!tiers.length) {
      original.call(this);
      this.refreshBundleCounts();
      return;
    }

    const count = this.discountCount;
    const last = tiers[tiers.length - 1].n;
    const current = this.currentTier;
    const next = this.nextTier;

    this.querySelector('[data-product-bundle-progress-bar]')?.style.setProperty('--progress', `${Math.min(count / last, 1) * 100}%`);
    this.querySelectorAll('[data-palier]').forEach((palier) => {
      palier.toggleAttribute('data-atteint', count >= parseInt(palier.dataset.palier, 10));
    });

    const text = this.querySelector('[data-product-bundle-discount-text]');
    if (text) {
      if (!next) {
        text.textContent = remplir(this.dataset.discountTextReached, { remise: current.pct });
      }
      else {
        const remaining = next.n - count;
        const modele = current && this.dataset.discountTextNext ? this.dataset.discountTextNext : this.dataset.discountTextRemaining;
        text.textContent = remplir(modele, { count: remaining, s: remaining > 1 ? 's' : '', remise: next.pct, actuelle: current?.pct ?? '' });
      }
    }

    this.refreshBundleCounts();
  });

  // Remise : au clic sur « Ajouter au panier », le code du palier atteint est ajouté au panier
  // (poserCode, à côté des codes des autres offres) avant l'ajout des articles. Shopify le garde et
  // l'applique dès que le panier y a droit, donc le panier latéral s'ouvre déjà remisé. Écoute en
  // capture : passe avant le clic du thème, puis relance ce clic une fois le code posé.
  document.addEventListener('click', async (event) => {
    const button = event.target.closest?.('[data-product-bundle-submit]');
    const bundle = button?.closest('product-bundle');
    const code = bundle?.currentTier?.code;
    if (!code) return;

    if (bundle.discountReady) {
      bundle.discountReady = false;
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    try {
      await poserCode(code);
    } catch (error) {
      console.log(error);
    }

    bundle.discountReady = true;
    button.click();
  }, true);

  // Fenêtre ouverte depuis le panneau : la couleur du premier module déjà choisi est proposée
  // d'office, pour que le bar reste d'une teinte. Le client peut en choisir une autre.
  document.addEventListener('quickview:loaded', () => {
    const fenetre = document.querySelector('.quick-view[data-context="bundle"]');
    if (!fenetre) return;
    const couleur = [...document.querySelectorAll('product-bundle [data-product-bundle-variant]:not([available])')]
      .map((ligne) => ligne.bundleColor).find(Boolean);
    if (!couleur) return;
    const choix = [...fenetre.querySelectorAll('variant-picker input[type="radio"]')].find((input) => input.value === couleur);
    if (choix && !choix.checked) choix.click();
  });


  /* 7. Canapés modulaires (sections/canape-modulaire.liquid, snippets/canape-compositions et canape-comprend)
     ------------------------------------------------------------ */

  // Page d'un canapé modulable : la fiche du thème (sections/main-product.liquid) porte les 990 variantes du hub
  // (composition × couleur de structure × revêtement) : galerie, prix, disponibilité et choix sont les siens.
  // Ici : les tuiles de composition et « Ce canapé comprend », l'ajout au panier et le devis de la composition
  // choisie (une ligne par module, avec la remise d'ensemble : la commande reste celle des modules) et
  // « Composer librement » (modules un par un). La variante d'un module vient de
  // templates/product.variante-json.liquid (?option_values=), qui passe au-delà des 250 variantes.
  const canape = document.querySelector('canape-modulaire');
  if (!canape) return;

  let donneesCanape = { modules: {} };
  try {
    donneesCanape = JSON.parse(canape.querySelector('[data-canape-modules]').textContent);
  } catch (error) {
    donneesCanape = { modules: {} };
  }
  const modulesCanape = donneesCanape.modules || {};
  const tauxCanape = parseInt(canape.dataset.taux, 10) || 0;
  // Client pro (mode-prix.liquid) : prix HT avec la mention « HT », et pour un pro soumis à la TVA française
  // le rappel du prix TTC au taux réel du produit, comme sur les fiches.
  const modePrixCanape = canape.dataset.modePrix || 'ttc';
  const proCanape = modePrixCanape !== 'ttc';
  const tauxReelCanape = parseInt(canape.dataset.tauxReel, 10) || 20;
  const motHt = canape.dataset.txtHt || 'HT';
  const rappelTtc = (centimes) => (modePrixCanape === 'ht-france'
    ? (canape.dataset.txtTtc || '[prix] TTC').replace('[prix]', formatMoney(Math.floor(Math.round(centimes * (100 + tauxReelCanape)) / 100)))
    : '');
  const remiseCanape = {
    code: canape.dataset.remiseCode || '',
    taux: parseFloat(canape.dataset.remiseTaux) || 0,
    seuil: parseInt(canape.dataset.remiseSeuil, 10) || 3
  };
  // Même arrondi que le panneau de composition (assets/theme.js, ProductBundle.addVariant).
  const ttcCanape = (centimes) => Math.floor(Math.round(centimes * (100 + tauxCanape)) / 100);

  // ---- Choix de la fiche : la variante du hub (option1 composition, option2 couleur, option3 revêtement).
  // Les modules ont les mêmes options « Couleur structure » et « Revêtement coussin », plus une finition fixée à
  // « Mate » (le laqué se fait sur devis).
  const lireVarianteHub = () => {
    try {
      return JSON.parse(document.querySelector('variant-picker [data-selected-variant]').textContent);
    } catch (error) {
      return null;
    }
  };
  let varianteHub = lireVarianteHub();
  // Options du hub après « Composition » : « Couleur structure » puis « Revêtement coussin » (Faz et la plupart des canapés), ou
  // seulement le revêtement (Milos, Suave, Tablet : polyuréthane, pas de couleur de structure). Leurs noms sont ceux des
  // options des modules : on les lit sur la fiche au lieu de les supposer.
  let nomsOptionsHub = ['Composition', 'Couleur structure', 'Revêtement coussin'];
  try {
    const lus = JSON.parse(canape.dataset.optionsHub || '[]');
    if (Array.isArray(lus) && lus.length > 1) nomsOptionsHub = lus;
  } catch (error) {
    // options par défaut
  }
  // Choix du hub (couleur, revêtement). La finition des modules est toujours la finition mate : son option
  // et sa valeur se reconnaissent dans toutes les langues vendues (« Finition », « Oberfläche », « Acabado »,
  // « Afwerking », « Acabamento », « Finitura » ; « Mate », « Matt », « Mat », « Opaco », « Fosco »).
  const choixCanape = {};
  const OPTION_FINITION = /^(finition|oberfl|gestelloberfl|acabado|afwerking|acabamento|finitura)/i;
  const VALEUR_MATE = /^(mate?|matt|opac[oa]|fosc[oa])$/i;
  // Taille d'un module (pouf Tablet, Pixel, The Factory, modules XL) : sans choix, Shopify prend la première valeur de l'option,
  // celle des compositions. Dans « Composer librement », la carte du module propose ses tailles (boutons) et chaque taille a sa
  // ligne. L'option est désignée par le thème (snippets/canape-option-taille.liquid, « taille » des données du module) ; à
  // défaut, même règle ici : son nom dans une des langues vendues, ou des valeurs qui sont des cotes (« 104x104x38 cm ») ou XL.
  const OPTION_TAILLE = /^(taille|gr(ö|oe)(ss|ß)e|size|tama[ñn]o|maat|afmetingen?|tamanho|taglia|dimension[ei]|dimens(õ|o)es|medidas?|abmessungen?)$/i;
  const VALEUR_COTE = /^(\s*[ø⌀]?\s*\d[^x]*x.*|xx?l)$/i;
  const optionTaille = (module) => {
    if (module && 'taille' in module) return module.taille || undefined;
    return Object.keys(module?.options || {}).find((nom) => {
      const valeurs = Object.keys(module.options[nom] || {});
      return valeurs.length > 1 && (OPTION_TAILLE.test(nom.trim()) || valeurs.every((valeur) => VALEUR_COTE.test(valeur.trim())));
    });
  };
  const taillesModule = (module) => { const nom = optionTaille(module); return nom ? Object.keys(module.options[nom]) : [null]; };
  const choixModule = (module, taille = null) => {
    const choix = Object.entries(choixCanape);
    if (taille && optionTaille(module)) choix.push([optionTaille(module), taille]);
    Object.entries(module?.options || {}).forEach(([nom, valeurs]) => {
      if (!OPTION_FINITION.test(nom) || choixCanape[nom]) return;
      const mate = Object.keys(valeurs || {}).find((valeur) => VALEUR_MATE.test(valeur));
      if (mate) choix.push([nom, mate]);
    });
    return choix;
  };
  const majChoix = () => {
    nomsOptionsHub.slice(1).forEach((nom, index) => {
      choixCanape[nom] = varianteHub?.[`option${index + 2}`];
    });
  };
  majChoix();
  const cleCouleur = (valeur) => (valeur || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  // Hub avec couleur de structure (pas Milos, Suave, Tablet, dont la 2e option est le revêtement), nom traduit compris.
  const aCouleurStructure = VERANO_COULEUR.test(nomsOptionsHub[1] || '');

  // Teinte du tissu choisie dans le nuancier de la fiche : propriétés de ligne (nom et code, comme les fiches)
  // et codes pour le SKU complet de chaque module ; « manquante » si un nuancier attend un choix.
  const teinteCanape = () => {
    const proprietes = {};
    const codes = [];
    let manquante = false;
    document.querySelectorAll('product-info [data-nuanciers] [data-nuancier]:not([hidden])').forEach((groupe) => {
      const teinte = groupe.querySelector('[data-nuancier-teinte]');
      const code = groupe.querySelector('[data-nuancier-code]');
      if (!teinte?.value) {
        manquante = true;
        return;
      }
      const nom = (input) => input.name.replace(/^properties\[(.*)\]$/, '$1');
      proprietes[nom(teinte)] = teinte.value;
      if (code?.value) {
        proprietes[nom(code)] = code.value;
        codes.push(code.value);
      }
    });
    return { proprietes, codes, manquante };
  };
  const proprietesModule = (variante, base = {}) => {
    const { proprietes, codes } = teinteCanape();
    const resultat = { ...base, ...proprietes };
    if (codes.length && variante?.sku) resultat['_SKU complet'] = [variante.sku, ...codes].join('_');
    return resultat;
  };

  const idsValeurs = (module, taille = null) => choixModule(module, taille)
    .map(([nom, valeur]) => module.options?.[nom]?.[valeur])
    .filter(Boolean);

  const variantesCanape = new Map();
  const varianteModule = (module, taille = null) => {
    // Vente directe (Delta) : la ligne de composition pointe vers la fiche elle-même (chaque composition est un produit
    // Vondom entier, avec sa référence et son prix) ; sa variante est celle choisie dans la fiche.
    if (String(module.id) === canape.dataset.produit) return Promise.resolve(varianteHub ? { ...varianteHub, module } : null);
    const ids = idsValeurs(module, taille);
    const cle = `${module.id}|${ids.join(',')}`;
    if (!variantesCanape.has(cle)) {
      // « canape=2 » : adresse distincte des réponses mises en cache par un ancien appel.
      const url = `${module.url.split('?')[0]}?view=variante-json&canape=2&option_values=${ids.join(',')}`;
      variantesCanape.set(cle, fetch(url)
        .then((reponse) => (reponse.ok ? reponse.json() : null))
        .then((variante) => (variante && variante.id && variante.options ? { ...variante, module, taille } : null))
        .catch(() => null)
        .then((variante) => {
          // Un échec (réseau, limite de Shopify) n'est pas gardé : le prochain changement de choix réessaie.
          if (!variante) variantesCanape.delete(cle);
          return variante;
        }));
    }
    return variantesCanape.get(cle);
  };
  // Variante trouvée et conforme au choix : Shopify retombe sur la première variante disponible
  // si la combinaison n'existe pas. Le fauteuil n'a pas d'option Finition : seules les options
  // que le module possède comptent.
  const varianteConforme = (variante) => !!variante && variante.available
    && choixModule(variante.module, variante.taille).every(([nom, valeur]) => !variante.module?.options?.[nom] || variante.options.includes(valeur));

  // ---- Compositions : tuiles au-dessus des choix, « Ce canapé comprend » dessous.
  const lignesComposition = (tuile) => {
    try {
      return JSON.parse(tuile?.dataset.lignes || '[]');
    } catch (error) {
      return [];
    }
  };
  const libelleComposition = (tuile) => `${canape.dataset.nom} · ${tuile.dataset.nom}${tuile.dataset.cotes ? ` (${tuile.dataset.cotes} cm)` : ''}`;
  const tuileChoisie = () => document.querySelector('[data-composition][aria-pressed="true"]') || document.querySelector('[data-composition]');
  // Couleur des fichiers d'images des tuiles (canape-modulable-<composition>-<couleur>.webp) : les noms de
  // fichiers sont en français. En français, la valeur d'option suffit ; dans une autre langue (/de/ : « Schwarz »),
  // on la lit dans le nom de fichier de l'image de la variante choisie (data-selected-variant-image), qui commence
  // par le même préfixe que la tuile de la composition choisie. Sans correspondance, l'image écru reste affichée.
  const nomFichier = (url) => String(url || '').split('?')[0].split('/').pop();
  const couleurFichier = () => {
    // Raccourci réservé au français : une fiche dont le nom d'option n'est pas traduit (Delta en espagnol :
    // « Couleur structure » mais valeur « Negro ») donnerait sinon un fichier « -negro.webp » qui n'existe pas.
    if (/^fr/i.test(document.documentElement.lang || '') && /^couleur structure$/i.test(nomsOptionsHub[1] || '')) return cleCouleur(varianteHub?.option2);
    let image = '';
    try {
      image = JSON.parse(document.querySelector('variant-picker [data-selected-variant-image]')?.textContent || '""');
    } catch (error) {
      image = '';
    }
    const fichier = nomFichier(image);
    const tuile = [...document.querySelectorAll('[data-composition]')].find((t) => t.dataset.option === varianteHub?.option1)
      || tuileChoisie();
    const prefixe = nomFichier(tuile?.dataset.images);
    if (!fichier || !prefixe || !fichier.startsWith(prefixe)) return '';
    return fichier.slice(prefixe.length).replace(/\.(webp|jpe?g|png)$/i, '');
  };
  // Plus de six compositions : les suivantes sont repliées derrière « Voir toutes les compositions », et le même
  // bouton les replie (« Voir moins de compositions »). La tuile choisie (adresse de la variante, retour de
  // l'acheteur) n'est jamais cachée.
  const basculerCompositions = (deplier) => {
    document.querySelectorAll('[data-composition-plus]').forEach((tuile) => {
      tuile.hidden = !deplier && tuile.getAttribute('aria-pressed') !== 'true';
    });
    document.querySelectorAll('.canape__tuiles').forEach((grille) => { grille.toggleAttribute('data-deplie', deplier); });
    document.querySelectorAll('[data-compositions-plus]').forEach((bouton) => {
      bouton.setAttribute('aria-expanded', deplier ? 'true' : 'false');
      const texte = bouton.querySelector('[data-compositions-plus-texte]');
      if (texte) texte.textContent = deplier ? texte.dataset.texteMoins : texte.dataset.textePlus;
    });
  };
  const deplierCompositions = () => basculerCompositions(true);
  if (document.querySelector('[data-composition-plus][aria-pressed="true"]')) deplierCompositions();
  document.addEventListener('click', (event) => {
    const bouton = event.target.closest?.('[data-compositions-plus]');
    if (!bouton) return;
    const deplier = bouton.getAttribute('aria-expanded') !== 'true';
    basculerCompositions(deplier);
    // En repliant, la page raccourcit : on garde le bouton à l'écran.
    if (!deplier) bouton.scrollIntoView({ block: 'nearest' });
  });
  const majTuiles = () => {
    const couleur = aCouleurStructure ? couleurFichier() : '';
    document.querySelectorAll('[data-composition]').forEach((tuile) => {
      tuile.setAttribute('aria-pressed', tuile.dataset.option === varianteHub?.option1 ? 'true' : 'false');
      const image = tuile.querySelector('.canape__tuile-img');
      if (image && tuile.dataset.images && couleur) {
        image.removeAttribute('srcset');
        image.src = `${tuile.dataset.images}${couleur}.webp?width=450`;
      }
    });
    document.querySelectorAll('[data-compo-nom]').forEach((element) => { element.textContent = tuileChoisie()?.dataset.nom || ''; });
    // Mobile : la ligne de tuiles défile ; la tuile choisie (adresse de la variante, autre choix) est ramenée dans la vue,
    // sans faire défiler la page.
    document.querySelectorAll('.canape__tuiles').forEach((ligne) => {
      const tuile = ligne.querySelector('[data-composition][aria-pressed="true"]');
      if (!tuile || ligne.scrollWidth <= ligne.clientWidth) return;
      const debut = tuile.offsetLeft;
      const fin = debut + tuile.offsetWidth;
      // Sans animation : un défilement « smooth » lancé pendant le changement de variante restait sur place.
      if (debut < ligne.scrollLeft) ligne.scrollLeft = debut - 8;
      else if (fin > ligne.scrollLeft + ligne.clientWidth) ligne.scrollLeft = fin - ligne.clientWidth + 8;
    });
    // Liste repliée : une composition cachée choisie autrement que par sa tuile (liste des options, adresse)
    // déplie la liste ; sinon on recache la tuile qui n'est plus choisie.
    if (document.querySelector('[data-compositions-plus][aria-expanded="false"]')) {
      if (document.querySelector('[data-composition-plus][hidden][aria-pressed="true"]')) deplierCompositions();
      else basculerCompositions(false);
    }
  };
  const majComprend = () => {
    const tuile = tuileChoisie();
    const boite = document.querySelector('[data-canape-comprend]');
    if (!tuile || !boite) return;
    const lignes = lignesComposition(tuile);
    const textes = (tuile.dataset.comprend || '').split('|').filter(Boolean);
    boite.querySelector('[data-comprend-liste]').innerHTML = textes
      .map((ligne, index) => {
        const id = textes.length === lignes.length ? lignes[index].module : '';
        const [, quantite, texte] = ligne.match(/^(\d+ ×) (.*)$/) || [];
        // « Module Gauche ~ 152 × 103 × 63 cm » : la taille va en petit sous le nom.
        const [nom, taille] = (texte || '').split(' ~ ');
        const petite = taille ? `<small class="canape__taille-ligne">${escapeHtml(taille)}</small>` : '';
        return quantite && id && modulesCanape[id] ? `<li><b>${quantite}</b> <button type="button" class="link" data-fiche-module="${id}">${escapeHtml(nom)}</button>${petite}</li>` : `<li>${escapeHtml(ligne).replace(' ~ ', ' ').replace(/^(\d+ ×)/, '<b>$1</b>')}</li>`;
      }).join('');
    const cotesTexte = tuile.dataset.precisionCotes
      ? veranoT('dimensions_hors_tout_precision', 'Dimensions hors tout : [cotes] cm ([precision])', { cotes: tuile.dataset.cotes, precision: tuile.dataset.precisionCotes })
      : veranoT('dimensions_hors_tout', 'Dimensions hors tout : [cotes] cm', { cotes: tuile.dataset.cotes });
    boite.querySelector('[data-comprend-cotes]').textContent = [tuile.dataset.cotes && cotesTexte, tuile.dataset.places && (tuile.dataset.places === '1' ? veranoT('places_un', '[count] place', { count: 1 }) : veranoT('places_n', '[count] places', { count: tuile.dataset.places }))].filter(Boolean).join(' · ');
    const remise = boite.querySelector('[data-comprend-remise]');
    remise.textContent = veranoT('remise_ensemble', '−[remise] % de remise d’ensemble, appliquée automatiquement dans le panier.', { remise: remiseCanape.taux });
    remise.hidden = !(remiseCanape.code && remiseCanape.taux && new Set(lignes.map((ligne) => ligne.module)).size >= remiseCanape.seuil);
  };
  // Un clic sur une tuile choisit la composition dans la fiche du thème (l'option « Composition » du hub est masquée).
  const choisirComposition = (tuile) => {
    // Option 1 du hub, repérée par sa position : son nom est traduit (« Zusammenstellung » sur /de/).
    const groupe = document.querySelector('variant-picker [data-option-position="1"]')
      || document.querySelector('variant-picker [data-option-slug="composition"]');
    const liste = groupe?.querySelector('select');
    if (liste) {
      liste.value = tuile.dataset.option;
      liste.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      groupe?.querySelector(`input[value="${CSS.escape(tuile.dataset.option)}"]`)?.click();
    }
  };

  // ---- Panier et devis (lignes = [{ module, quantite, variante }]).
  const ajouterLignes = async (lignes, bouton, erreur, libelle) => {
    if (!lignes.length || !lignes.every((ligne) => varianteConforme(ligne.variante))) return;
    erreur.hidden = true;
    if (teinteCanape().manquante) {
      erreur.innerHTML = `${veranoT('teinte_requise', 'Choisissez d’abord la teinte du tissu, dans le haut de la page.')} <button type="button" class="link" data-vers-teinte>${veranoT('choisir_teinte', 'Choisir la teinte')}</button>`;
      erreur.hidden = false;
      return;
    }
    // Comme le thème (ProductForm) : aria-disabled et aria-busy, jamais « disabled » ; sur la fiche, un bouton
    // désactivé passe pour un produit épuisé et le bloc d'achat affiche « Épuisé - Prévenez-moi ».
    if (bouton.getAttribute('aria-disabled') === 'true') return;
    bouton.setAttribute('aria-disabled', 'true');
    bouton.setAttribute('aria-busy', 'true');
    try {
      const differents = new Set(lignes.map((ligne) => ligne.module)).size;
      if (remiseCanape.code && differents >= remiseCanape.seuil) await poserCode(remiseCanape.code);
      const sections = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections } }));
      const reponse = await fetch(theme.routes.cart_add_url, {
        ...theme.utils.fetchConfig('javascript'),
        body: JSON.stringify({
          items: lignes.map((ligne) => ({ id: ligne.variante.id, quantity: ligne.quantite, properties: proprietesModule(ligne.variante, { _Composition: libelle }) })),
          sections,
          sections_url: window.location.pathname
        })
      });
      const etat = await reponse.json();
      if (etat.status) {
        erreur.textContent = etat.description || etat.message || '';
        erreur.hidden = false;
        return;
      }
      const panier = await (await fetch(theme.routes.cart_url, theme.utils.fetchConfig('json', 'GET'))).json();
      panier.sections = etat.sections;
      theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'canape-modulaire', cart: panier });
      document.querySelector('cart-drawer')?.show(bouton);
    } catch (error) {
      console.error(error);
    } finally {
      bouton.removeAttribute('aria-busy');
      bouton.removeAttribute('aria-disabled');
    }
  };
  // Devis : une ligne par module, avec les teintes du tissu, comme depuis une fiche.
  const devisLignes = (lignes, bouton) => {
    const { proprietes } = teinteCanape();
    const options = Object.entries(proprietes).filter(([cle]) => !cle.startsWith('_')).map(([name, value]) => ({ name, value, code: proprietes[`_Code ${name}`] || '' }));
    addToQuote(lignes.map((ligne) => {
      const module = modulesCanape[ligne.module];
      return {
        id: ligne.variante.id,
        options,
        product: module.titre,
        variant: ligne.variante.title,
        quantity: ligne.quantite,
        lotSize: 0,
        lotPlural: '',
        image: ligne.variante.image || module.image,
        url: `${module.url.split('?')[0]}?variant=${ligne.variante.id}`,
        price: ttcCanape(ligne.variante.price),
        taux: tauxCanape,
        supplements: []
      };
    }));
    openQuote(bouton);
  };

  const lignesFiche = async () => {
    const tuile = tuileChoisie();
    const lignes = lignesComposition(tuile);
    const variantes = await Promise.all(lignes.map((ligne) => (modulesCanape[ligne.module] ? varianteModule(modulesCanape[ligne.module], ligne.taille || null) : null)));
    return lignes.map((ligne, index) => ({ module: ligne.module, taille: ligne.taille || null, quantite: ligne.quantite, variante: variantes[index] }));
  };
  // Le bouton d'ajout et le bouton devis de la fiche du thème ajoutent une ligne par module (et non la
  // variante du hub) : la remise d'ensemble et le passage de commande ne changent pas.
  const erreurFiche = (formulaire) => {
    let erreur = formulaire.querySelector('.product-form__error-message');
    if (!erreur) {
      erreur = document.createElement('p');
      erreur.className = 'product-form__error-message';
      formulaire.append(erreur);
    }
    erreur.setAttribute('data-canape-erreur', '');
    return erreur;
  };
  document.addEventListener('submit', async (event) => {
    const formulaire = event.target;
    if (!varianteHub || !formulaire.matches?.('form[is="product-form"]') || !formulaire.closest('product-info')) return;
    event.preventDefault();
    event.stopPropagation();
    const tuile = tuileChoisie();
    const erreur = erreurFiche(formulaire);
    const lignes = await lignesFiche();
    if (!lignes.length || !lignes.every((ligne) => varianteConforme(ligne.variante))) {
      erreur.textContent = veranoT('composition_indisponible', 'Cette composition n’est pas disponible dans cette couleur et ce revêtement.');
      erreur.hidden = false;
      return;
    }
    ajouterLignes(lignes, formulaire.querySelector('[type="submit"]'), erreur, libelleComposition(tuile));
  }, true);
  document.addEventListener('click', async (event) => {
    const bouton = event.target.closest?.('[data-quote-add]');
    if (!varianteHub || !bouton?.closest('product-info')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const lignes = await lignesFiche();
    if (lignes.length && lignes.every((ligne) => varianteConforme(ligne.variante))) devisLignes(lignes, bouton);
  }, true);

  // ---- Composer librement : quantités par module, pré-remplies avec la composition choisie.
  const libre = { quantites: {}, modifie: false, lignes: [] };
  // Clé d'une ligne libre : l'identifiant du module, suivi de sa taille pour un module à tailles (« 123~104x104x38 cm »).
  const tailleChoisie = {};
  // Taille montrée par la carte : celle choisie par le client, sinon celle que prend la composition (« 3 places XL »), sinon la première.
  const tailleCarte = (module) => {
    if (!optionTaille(module)) return null;
    if (tailleChoisie[module.id]) return tailleChoisie[module.id];
    return taillesModule(module).find((taille) => libre.quantites[cleLibre(module, taille)] > 0) || taillesModule(module)[0];
  };
  const cleLibre = (module, taille) => (taille ? `${module.id}~${taille}` : String(module.id));
  const blocLibre = canape.querySelector('[data-canape-libre]');
  // Ordre des modules (récapitulatif) : celui des cartes, d'après le rôle du module (métachamp custom.role_module, lu par
  // snippets/canape-module-role.liquid), qui ne dépend pas de la langue : gauche, central, droit, angle (90° puis 45°), pouf, fauteuil.
  const ORDRE_ROLES = ['gauche', 'central', 'droit', 'angle', null, 'pouf', 'fauteuil'];
  const rang = (module) => {
    const trouve = ORDRE_ROLES.indexOf(module.role || 'autre');
    if (trouve < 0) return 9;
    return module.role === 'angle' && module.titre.includes('45') ? 4 : trouve;
  };
  const modulesLibres = Object.values(modulesCanape).sort((a, b) => rang(a) - rang(b));
  const nomModule = (module) => (module.titre.split(' - ')[0] === canape.dataset.nom ? module.nom : module.titre.split(' - ')[0]);
  const depuisComposition = () => {
    libre.quantites = {};
    // Une composition prend la taille de sa ligne, à défaut la première du module (celle que Shopify choisit sans taille).
    lignesComposition(tuileChoisie()).forEach((ligne) => {
      const module = modulesCanape[ligne.module];
      const cle = module ? cleLibre(module, optionTaille(module) ? ligne.taille || taillesModule(module)[0] : null) : String(ligne.module);
      libre.quantites[cle] = (libre.quantites[cle] || 0) + ligne.quantite;
    });
    libre.modifie = false;
  };
  let generationLibre = 0;
  const majLibre = async () => {
    if (!blocLibre) return;
    generationLibre += 1;
    const generation = generationLibre;
    // Une entrée par module et par taille : la carte montre celle de la taille choisie, le récapitulatif toutes celles choisies.
    const entrees = modulesLibres.flatMap((module) => taillesModule(module).map((taille) => ({ module, taille, cle: cleLibre(module, taille) })));
    const variantesEntrees = await Promise.all(entrees.map((entree) => varianteModule(entree.module, entree.taille)));
    if (generation !== generationLibre) return;
    const entree = (module, taille) => entrees.findIndex((e) => e.module === module && e.taille === taille);
    // Cartes des modules (rendues par Liquid) : prix de la couleur et du revêtement choisis, quantité, image de la couleur.
    blocLibre.querySelectorAll('.canape__module').forEach((carte) => {
      const module = modulesCanape[carte.dataset.module];
      if (!module) return;
      const taille = tailleCarte(module);
      const variante = variantesEntrees[entree(module, taille)];
      const quantite = libre.quantites[cleLibre(module, taille)] || 0;
      carte.querySelectorAll('[data-taille]').forEach((bouton) => bouton.setAttribute('aria-pressed', bouton.dataset.taille === taille));
      carte.querySelectorAll('[data-schema-taille]').forEach((figure) => { figure.hidden = figure.dataset.schemaTaille !== taille; });
      const ok = varianteConforme(variante);
      carte.classList.toggle('is-choisi', quantite > 0);
      carte.querySelector('[data-module-prix]').textContent = ok ? `${formatMoney(ttcCanape(variante.price))}${proCanape ? ` ${motHt}` : ''}` : veranoT('indisponible', 'Indisponible');
      carte.querySelector('[data-module-qte]').textContent = quantite;
      carte.querySelector('[data-pas="-1"]').disabled = !quantite;
      carte.querySelector('[data-pas="1"]').disabled = !ok;
      const badge = carte.querySelector('[data-bundle-count]');
      if (badge) {
        badge.hidden = !quantite;
        const texte = badge.querySelector('[data-bundle-count-texte]');
        if (texte) texte.textContent = veranoTn('selectionnes', quantite, '[count] sélectionné', '[count] sélectionnés');
      }
      const image = carte.querySelector('.product-card__media img');
      if (image && variante?.image && image.dataset.couleurSrc !== variante.image) {
        image.dataset.couleurSrc = variante.image;
        image.removeAttribute('srcset');
        image.src = `${variante.image}${variante.image.includes('?') ? '&' : '?'}width=450`;
      }
    });
    // Récapitulatif
    const lignes = entrees.map((e, index) => ({ module: e.module.id, quantite: libre.quantites[e.cle] || 0, variante: variantesEntrees[index], nom: nomModule(e.module), taille: e.taille ? e.taille.replace(/x/g, ' × ') : '', role: e.module.role || 'autre' }))
      .filter((ligne) => ligne.quantite > 0);
    libre.lignes = lignes;
    const brut = lignes.reduce((total, ligne) => total + (varianteConforme(ligne.variante) ? ttcCanape(ligne.variante.price) * ligne.quantite : 0), 0);
    const differents = lignes.length;
    const nombre = lignes.reduce((total, ligne) => total + ligne.quantite, 0);
    // Comme les compositions (« canapé 3 places et pouf ») : chaque module assis compte une place, le pouf aucune.
    const places = lignes.filter((ligne) => ligne.role !== 'pouf').reduce((total, ligne) => total + ligne.quantite, 0);
    const poufs = lignes.filter((ligne) => ligne.role === 'pouf').reduce((total, ligne) => total + ligne.quantite, 0);
    const textePlaces = places && veranoTn('places', places, '[count] place', '[count] places');
    const textePoufs = poufs && veranoTn('poufs', poufs, '[count] pouf', '[count] poufs');
    const libellePlaces = places && poufs ? veranoT('places_et_poufs', '[places] et [poufs]', { places: textePlaces, poufs: textePoufs }) : textePlaces || textePoufs || '';
    blocLibre.querySelector('[data-recap-vide]').hidden = lignes.length > 0;
    // Vignette : la première image de la galerie de la variante du module (celle de la carte), en petit.
    const vignette = (ligne) => {
      const source = ligne.variante?.image || modulesCanape[ligne.module]?.image;
      if (!source) return '';
      const petite = /[?&]width=\d+/.test(source) ? source.replace(/([?&]width=)\d+/, '$196') : `${source}${source.includes('?') ? '&' : '?'}width=96`;
      return `<img class="canape__recap-vignette" src="${petite}" alt="" width="48" height="48" loading="lazy">`;
    };
    blocLibre.querySelector('[data-recap-lignes]').innerHTML = lignes.map((ligne) => `<li>${vignette(ligne)}<span>${ligne.quantite} × <button type="button" class="link" data-fiche-module="${ligne.module}">${escapeHtml(ligne.nom)}</button>${ligne.taille ? `<small class="canape__taille-ligne">${escapeHtml(ligne.taille)}</small>` : ''}</span><span>${varianteConforme(ligne.variante) ? formatMoney(ttcCanape(ligne.variante.price) * ligne.quantite) : ''}</span></li>`).join('');
    const choix = Object.entries(choixCanape).filter(([nom]) => !OPTION_FINITION.test(nom)).map(([, valeur]) => valeur).join(' · ');
    blocLibre.querySelector('[data-recap-infos]').textContent = lignes.length ? `${libellePlaces} · ${choix}` : '';
    // Sous-total, remise d'ensemble et total, dans cet ordre ; la barre suit le nombre de modules différents.
    const remiseAtteinte = !!remiseCanape.code && !!remiseCanape.taux && differents >= remiseCanape.seuil;
    const remise = remiseAtteinte ? Math.round(brut * remiseCanape.taux / 100) : 0;
    blocLibre.querySelector('[data-recap-sous]').hidden = !lignes.length;
    blocLibre.querySelector('[data-recap-total-bloc]').hidden = !lignes.length;
    blocLibre.querySelector('[data-recap-nombre]').textContent = veranoTn('nb_modules', nombre, '[count] module', '[count] modules');
    blocLibre.querySelector('[data-recap-soustotal]').textContent = formatMoney(brut);
    blocLibre.querySelector('[data-recap-remise]').hidden = !remise;
    blocLibre.querySelector('[data-recap-remise-libelle]').textContent = veranoT('remise_libelle', 'Remise −[remise] %', { remise: remiseCanape.taux });
    blocLibre.querySelector('[data-recap-remise-montant]').textContent = `−${formatMoney(remise)}`;
    blocLibre.querySelector('[data-recap-total]').textContent = formatMoney(brut - remise);
    blocLibre.querySelector('[data-recap-total-libelle]').textContent = proCanape ? veranoT('total_ht', 'Total [ht]', { ht: motHt }) : veranoT('total', 'Total');
    const ttcRecap = blocLibre.querySelector('[data-recap-ttc]');
    ttcRecap.textContent = lignes.length ? rappelTtc(brut - remise) : '';
    ttcRecap.hidden = !ttcRecap.textContent;
    const progression = blocLibre.querySelector('[data-recap-progression]');
    if (progression) {
      const manque = Math.max(remiseCanape.seuil - differents, 0);
      progression.querySelector('[data-recap-barre]').style.setProperty('--progression', Math.min(differents / remiseCanape.seuil, 1));
      progression.querySelectorAll('[data-palier]').forEach((palier) => palier.toggleAttribute('data-atteint', !manque));
      progression.querySelector('[data-recap-progression-texte]').textContent = manque
        ? veranoTn('progression', manque, 'Encore [count] module différent pour obtenir −[remise] %', 'Encore [count] modules différents pour obtenir −[remise] %', { remise: remiseCanape.taux })
        : veranoT('progression_atteinte', '−[remise] % appliqué à votre canapé', { remise: remiseCanape.taux });
    }
    const valide = lignes.length > 0 && lignes.every((ligne) => varianteConforme(ligne.variante));
    blocLibre.querySelector('[data-libre-ajouter]').disabled = !valide;
    blocLibre.querySelector('[data-libre-devis]').disabled = !valide;
    blocLibre.querySelector('[data-libre-ajouter] .btn-text').textContent = lignes.length
      ? veranoTn('ajouter_panier', nombre, 'Ajouter au panier ([count] module)', 'Ajouter au panier ([count] modules)')
      : veranoT('ajouter_panier', 'Ajouter au panier');
    blocLibre.querySelector('[data-libre-revenir]').hidden = !libre.modifie;
    blocLibre.querySelector('[data-libre-vider]').hidden = !lignes.length;
    // Minuscules en français (« la composition « canapé 3 places » ») ; en allemand les noms gardent leur majuscule.
    const nomCompo = tuileChoisie()?.dataset.nom || '';
    blocLibre.querySelector('[data-libre-compo]').textContent = document.documentElement.lang.startsWith('de') ? nomCompo : nomCompo.toLowerCase();
  };

  // ---- Changements
  theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.variantChange, ({ data }) => {
    if (!data?.variant || String(data.productId) !== canape.dataset.produit) return;
    varianteHub = data.variant;
    majChoix();
    majTuiles();
    majComprend();
    if (!libre.modifie) depuisComposition();
    majLibre();
  });
  // Couleur ou revêtement choisis : les cartes cherchent leurs variantes tout de suite, en même temps que la fiche
  // recharge la sienne (elle met de 0,1 à 1,5 s), au lieu d'attendre son événement.
  const optionsHub = Object.fromEntries(nomsOptionsHub.slice(1).map((nom) => [cleCouleur(nom), nom]));
  document.addEventListener('change', (event) => {
    const groupe = event.target.closest?.('product-info variant-picker [data-option-slug]');
    const nom = optionsHub[groupe?.dataset.optionSlug];
    if (!nom || !event.target.value) return;
    choixCanape[nom] = event.target.value;
    majLibre();
  });
  document.addEventListener('click', (event) => {
    const tuile = event.target.closest?.('[data-composition]');
    if (tuile) {
      choisirComposition(tuile);
      return;
    }
    if (event.target.closest?.('[data-nuancier-pastille]')) {
      setTimeout(() => document.querySelectorAll('[data-canape-erreur], [data-libre-erreur]').forEach((erreur) => { erreur.hidden = true; }), 0);
      return;
    }
    if (event.target.closest?.('[data-vers-teinte]')) {
      document.querySelector('product-info [data-nuancier]:not([hidden])')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    const fiche = event.target.closest?.('[data-fiche-module]');
    if (fiche) {
      canape.querySelector(`dialog[data-fiche="${CSS.escape(fiche.dataset.ficheModule)}"]`)?.showModal();
      return;
    }
    if (event.target.closest?.('[data-fiche-fermer]')) { event.target.closest('dialog').close(); return; }
    if (event.target.matches?.('dialog[data-fiche]')) { event.target.close(); return; }
    const pas = event.target.closest?.('[data-pas]');
    const choixTaille = event.target.closest?.('[data-taille-module]');
    if (choixTaille && modulesCanape[choixTaille.dataset.tailleModule]) {
      tailleChoisie[choixTaille.dataset.tailleModule] = choixTaille.dataset.taille;
      majLibre();
      return;
    }
    if (pas) {
      const module = modulesCanape[pas.dataset.module];
      const id = module ? cleLibre(module, tailleCarte(module)) : pas.dataset.module;
      libre.quantites[id] = Math.max(0, (libre.quantites[id] || 0) + parseInt(pas.dataset.pas, 10));
      libre.modifie = true;
      majLibre();
      return;
    }
    if (event.target.closest?.('[data-libre-revenir]')) { depuisComposition(); majLibre(); return; }
    if (event.target.closest?.('[data-libre-vider]')) { libre.quantites = {}; libre.modifie = true; majLibre(); return; }
    const laque = event.target.closest?.('[data-libre-laque]');
    if (laque) {
      const lignes = libre.lignes.filter((ligne) => varianteConforme(ligne.variante));
      if (lignes.length) devisLignes(lignes, laque);
      else openQuote(laque);
      return;
    }
    const ajoutLibre = event.target.closest?.('[data-libre-ajouter]');
    if (ajoutLibre && !ajoutLibre.disabled) {
      ajouterLignes(libre.lignes, ajoutLibre, blocLibre.querySelector('[data-libre-erreur]'), `${canape.dataset.nom} · Composition libre`);
      return;
    }
    const devisLibre = event.target.closest?.('[data-libre-devis]');
    if (devisLibre && !devisLibre.disabled) devisLignes(libre.lignes, devisLibre);
  });

  // « Ajouter à mon canapé » depuis la fenêtre de détail d'un module (théme : bundle:added, qui remonte
  // dans la page) : une unité de plus dans « Composer librement ».
  canape.addEventListener('bundle:added', (event) => {
    const id = event.detail?.product?.querySelector('product-card-info')?.dataset.productId;
    if (!id || !modulesCanape[id]) return;
    event.stopPropagation();
    const cle = cleLibre(modulesCanape[id], tailleCarte(modulesCanape[id]));
    libre.quantites[cle] = (libre.quantites[cle] || 0) + 1;
    libre.modifie = true;
    majLibre();
  });

  // Fenêtre de détail d'un module : elle s'ouvre sur la couleur et le revêtement du canapé
  // (option_values ajouté à l'appel ?view=modal) ; le choix des options n'y est pas proposé, pour que
  // tout le canapé reste d'une couleur.
  const moduleParUrl = (url) => Object.values(modulesCanape).find((module) => module.url.split('?')[0] === String(url || '').split('?')[0]);
  let vueCanape = null;
  wrap(customElements.get('quick-view'), 'quickview', function (original, ...args) {
    const module = moduleParUrl(this.getAttribute('data-product-url'));
    vueCanape = module ? { url: `${module.url.split('?')[0]}?view=modal`, ids: idsValeurs(module) } : null;
    return original.apply(this, args);
  });
  const fetchAvantCanape = window.fetch;
  window.fetch = function (ressource, ...reste) {
    if (vueCanape && typeof ressource === 'string' && ressource === vueCanape.url && vueCanape.ids.length) {
      ressource = `${ressource}&option_values=${vueCanape.ids.join(',')}`;
      vueCanape = null;
    }
    return fetchAvantCanape.call(this, ressource, ...reste);
  };
  document.addEventListener('quickview:loaded', (event) => {
    if (!moduleParUrl(event.detail?.productUrl)) return;
    // Le thème déplace la fenêtre hors de la section à l'ouverture : on la cherche dans toute la page.
    document.querySelectorAll('quick-view[data-context="bundle"]').forEach((fenetre) => {
      if (fenetre.getAttribute('data-product-url')?.split('?')[0] !== event.detail.productUrl.split('?')[0]) return;
      fenetre.classList.add('quick-view--canape');
      const selecteur = fenetre.querySelector('variant-picker');
      if (selecteur && !fenetre.querySelector('.canape__rappel')) {
        const rappel = document.createElement('p');
        rappel.className = 'canape__rappel text-sm';
        const teinte = Object.entries(teinteCanape().proprietes).find(([cle]) => !cle.startsWith('_'))?.[1];
        rappel.textContent = veranoT('rappel_finition', 'Finition du canapé : [choix]. Elle se change en haut de la page.', { choix: [...Object.values(choixCanape).filter((valeur) => valeur !== 'Mate'), teinte].filter(Boolean).join(' · ') });
        selecteur.insertAdjacentElement('beforebegin', rappel);
      }
      const libelle = fenetre.querySelector('.product-form-wrapper form button[type="submit"] .btn-text span');
      if (libelle) libelle.textContent = veranoT('ajouter_a_canape', 'Ajouter à mon canapé');
      // Même teinte de tissu que le canapé, choisie d'office dans la fenêtre.
      const codeTeinte = Object.entries(teinteCanape().proprietes).find(([cle]) => cle.startsWith('_Code'))?.[1];
      if (codeTeinte) {
        setTimeout(() => fenetre.querySelector(`[data-nuancier]:not([hidden]) [data-nuancier-pastille][data-code="${CSS.escape(codeTeinte)}"]`)?.click(), 150);
      }
    });
  });

  majTuiles();
  majComprend();
  depuisComposition();
  majLibre();
})();

// ---- Avant-première (snippets/avant-premiere.liquid) : l'inscription « me prévenir » part au formulaire HubSpot
// des devis (réglages de la section quote-drawer), message « Avant-première » avec le produit. L'encart affiche
// ensuite sa confirmation sur place ; en cas d'échec, un message avec le téléphone.
document.addEventListener('submit', async (event) => {
  const form = event.target.closest?.('[data-avant-premiere-form]');
  if (!form) return;
  event.preventDefault();
  const bloc = form.closest('[data-avant-premiere]');
  const champ = form.querySelector('input[type="email"]');
  const erreur = bloc.querySelector('[data-avant-premiere-erreur]');
  const bouton = form.querySelector('button[type="submit"]');
  erreur.hidden = true;
  if (!champ.checkValidity() || !champ.value.trim()) {
    champ.setAttribute('aria-invalid', 'true');
    erreur.textContent = veranoT('email_invalide', 'Indiquez une adresse e-mail valide.');
    erreur.hidden = false;
    champ.focus();
    return;
  }
  champ.removeAttribute('aria-invalid');
  if (bouton.getAttribute('aria-busy') === 'true') return;
  bouton.setAttribute('aria-busy', 'true');
  const devis = document.getElementById('QuoteDrawer');
  try {
    if (!devis?.dataset.hubspotPortal || !devis?.dataset.hubspotForm) throw new Error('HubSpot non configuré');
    const reponse = await fetch(`https://api.hsforms.com/submissions/v3/integration/submit/${devis.dataset.hubspotPortal}/${devis.dataset.hubspotForm}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        fields: [
          { name: 'email', value: champ.value.trim() },
          { name: 'message', value: `Avant-première : prévenir quand ce produit sera disponible.\n${bloc.dataset.produit} (${bloc.dataset.produitUrl})` }
        ],
        context: { pageUri: window.location.href, pageName: document.title }
      })
    });
    if (!reponse.ok) throw new Error(`HubSpot ${reponse.status}`);
    form.hidden = true;
    bloc.querySelector('[data-avant-premiere-note]').hidden = true;
    bloc.querySelector('[data-avant-premiere-merci]').hidden = false;
  } catch (error) {
    console.log(error);
    erreur.textContent = veranoT('erreur_inscription', 'Votre inscription n’a pas pu être enregistrée. Réessayez dans un instant, ou appelez-nous au +33 7 56 90 02 78.');
    erreur.hidden = false;
  } finally {
    bouton.removeAttribute('aria-busy');
  }
});

// ---- Boutons « Ajouter au devis » et téléphone : côte à côte quand ils tiennent, empilés quand le texte
// déborde (selon la langue et la largeur), plutôt que de couper le texte.
(() => {
  const largeurs = new WeakMap();
  const ajuster = (ligne) => {
    ligne.classList.remove('quote-call__buttons--empiles');
    const deborde = [...ligne.querySelectorAll('.quote-call__button')].some((bouton) => bouton.scrollWidth > bouton.clientWidth + 1);
    ligne.classList.toggle('quote-call__buttons--empiles', deborde);
  };
  const observateur = new ResizeObserver((entrees) => {
    entrees.forEach(({ target, contentRect }) => {
      // Empiler change la hauteur, pas la largeur : on ne remesure que si la largeur a changé.
      if (largeurs.get(target) === contentRect.width) return;
      largeurs.set(target, contentRect.width);
      ajuster(target);
    });
  });
  const suivre = (racine) => racine.querySelectorAll?.('.quote-call__buttons').forEach((ligne) => observateur.observe(ligne));
  suivre(document);
  // Les polices arrivent après le premier calcul et changent la largeur du texte.
  document.fonts?.ready.then(() => document.querySelectorAll('.quote-call__buttons').forEach(ajuster));
  // Fiches rechargées par le thème (changement de variante, fenêtre « Choisir des options ») : nouvelles lignes à suivre.
  new MutationObserver((mutations) => mutations.forEach((m) => m.addedNodes.forEach((n) => {
    if (n.nodeType !== 1) return;
    if (n.matches('.quote-call__buttons')) observateur.observe(n);
    suivre(n);
  }))).observe(document.body, { childList: true, subtree: true });
})();

// ---- Rubrique Fichiers 3D & Techniques (snippets/fichiers-techniques.liquid) : onglets Fiche technique /
// Fichiers 3D / Notice de montage, au clic et au clavier (flèches, Début, Fin), et liste des modules
// d'un canapé modulable (snippets/fichiers-3d.liquid) : « Voir les N autres modules » / « Réduire ».
// Délégué au document : les fenêtres de choix arrivent après le chargement de la page.
(() => {
  const choisir = (onglet, focus = false) => {
    const liste = onglet.closest('[role="tablist"]');
    liste.querySelectorAll('[role="tab"]').forEach((autre) => {
      const actif = autre === onglet;
      autre.setAttribute('aria-selected', String(actif));
      autre.tabIndex = actif ? 0 : -1;
      const panneau = document.getElementById(autre.getAttribute('aria-controls'));
      if (panneau) panneau.hidden = !actif;
    });
    onglet.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    if (focus) onglet.focus();
  };

  document.addEventListener('click', (event) => {
    const onglet = event.target.closest?.('[data-fichiers-techniques] [role="tab"]');
    if (onglet) {
      choisir(onglet);
      return;
    }
    const plus = event.target.closest?.('[data-modules-plus]');
    if (!plus) return;
    const ouvert = plus.getAttribute('aria-expanded') !== 'true';
    plus.setAttribute('aria-expanded', String(ouvert));
    document.getElementById(plus.getAttribute('aria-controls'))
      ?.querySelectorAll('[data-module-cache]').forEach((ligne) => { ligne.hidden = !ouvert; });
    plus.querySelector('[data-modules-plus-voir]').hidden = ouvert;
    plus.querySelector('[data-modules-plus-reduire]').hidden = !ouvert;
  });

  document.addEventListener('keydown', (event) => {
    const onglet = event.target.closest?.('[data-fichiers-techniques] [role="tab"]');
    if (!onglet) return;
    const onglets = [...onglet.closest('[role="tablist"]').querySelectorAll('[role="tab"]')];
    const i = onglets.indexOf(onglet);
    const cible = {
      ArrowRight: onglets[(i + 1) % onglets.length],
      ArrowLeft: onglets[(i - 1 + onglets.length) % onglets.length],
      Home: onglets[0],
      End: onglets[onglets.length - 1]
    }[event.key];
    if (!cible) return;
    event.preventDefault();
    choisir(cible, true);
  });
})();

// ---- WhatsApp (snippets/quote-call-buttons.liquid, barre fixe de sections/main-product.liquid,
// snippets/garanties-panier.liquid) : le message pré-rempli suit la variante choisie, et chaque clic envoie
// l'événement whatsapp_click (dataLayer, gtag s'il est chargé, et Shopify.analytics pour un pixel client),
// avec l'emplacement (fiche, barre fixe, panier), le produit, la variante, la valeur HT et la langue.
(() => {
  // Dernière variante choisie, par produit : la fiche ne recharge pas toujours le bouton au changement.
  const variantes = new Map();
  const boutonFiche = (produit) => document.querySelector(`[data-whatsapp="fiche"][data-product-id="${produit}"]`);
  const choix = (bouton) => {
    const variante = variantes.get(bouton.dataset.productId);
    return {
      id: variante?.id ?? bouton.dataset.variantId,
      titre: variante ? (variante.title === 'Default Title' ? '' : variante.title) : bouton.dataset.variante,
      valeur: variante ? variante.price / 100 : Number(bouton.dataset.valeur)
    };
  };
  const lien = (bouton) => {
    const { id, titre } = choix(bouton);
    const produit = titre ? `${bouton.dataset.produit} (${titre})` : bouton.dataset.produit;
    const message = `${bouton.dataset.modele.split('[produit]').join(produit)}\n${bouton.dataset.url}?variant=${id}`;
    return bouton.dataset.numero + encodeURIComponent(message);
  };
  // Le bouton de la fiche et le rond de la barre fixe d'un même produit portent le même lien.
  const majLiens = (produit) => {
    const fiche = boutonFiche(produit);
    if (!fiche) return;
    const href = lien(fiche);
    document.querySelectorAll(`[data-whatsapp][data-product-id="${produit}"]`).forEach((a) => { a.href = href; });
  };
  document.querySelectorAll('[data-whatsapp="fiche"]').forEach((a) => majLiens(a.dataset.productId));
  theme.pubsub?.subscribe(theme.pubsub.PUB_SUB_EVENTS.variantChange, ({ data }) => {
    if (!data?.variant || !data.productId) return;
    variantes.set(String(data.productId), data.variant);
    setTimeout(() => majLiens(String(data.productId)), 50);
  });

  document.addEventListener('click', (event) => {
    const a = event.target.closest?.('a[data-whatsapp]');
    if (!a) return;
    const fiche = a.dataset.productId && boutonFiche(a.dataset.productId);
    // Le lien est refait juste avant que le navigateur ne le suive.
    if (fiche) majLiens(a.dataset.productId);
    const { titre, valeur } = fiche ? choix(fiche) : { titre: '', valeur: undefined };
    const donnees = {
      emplacement: a.dataset.whatsapp,
      produit: fiche?.dataset.produit || '',
      variante: titre || '',
      valeur: Number.isFinite(valeur) ? valeur : undefined,
      currency: window.Shopify?.currency?.active || 'EUR',
      langue: document.documentElement.lang || ''
    };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'whatsapp_click', ...donnees });
    if (typeof window.gtag === 'function') window.gtag('event', 'whatsapp_click', { ...donnees, value: donnees.valeur });
    window.Shopify?.analytics?.publish?.('whatsapp_click', donnees);
  }, true);
})();

/*
 * 10. Sélecteur compact : le nom de la couleur choisie suit le clic sans attendre le rechargement
 *     de la section (snippets/product-variant-picker-swatch.liquid, « Couleur · Noir »).
 */
(() => {
  document.addEventListener('change', (event) => {
    const radio = event.target;
    if (!radio.matches?.('.variant-picker--compact fieldset input[type="radio"]')) return;
    const nom = radio.closest('fieldset')?.querySelector('.option-valeur');
    if (nom) nom.textContent = radio.value;
  });
})();
